import type { ServiceAreaSlug } from "@/config/service-areas";
import type { ServicePage } from "@/config/services";

export type ServiceSlug =
  | "roof-replacement"
  | "roof-repair"
  | "asphalt-shingle-roofing"
  | "metal-roofing"
  | "flat-roofing"
  | "roof-inspection-maintenance"
  | "flashing-roof-penetration-repair"
  | "moss-removal-roof-gutter"
  | "roof-health-check-maintenance"
  | "shingle-spot-repair"
  | "gutter-cleaning-downspout-flush"
  | "storm-tarping-emergency-dry-in"
  | "skylight-leak-flashing-repair"
  | "chimney-flashing-repair"
  | "gutter-replacement"
  | "gutter-guard-installation"
  | "roof-ventilation-upgrade"
  | "fascia-soffit-repair-replacement"
  | "cedar-shake-repair-restoration"
  | "flat-low-slope-roof-repair"
  | "metal-roof-repair-maintenance"
  | "cedar-shake-roof-replacement";

export type ServiceContentTranslation = Partial<
  Omit<
    ServicePage,
    "slug" | "image" | "path" | "category" | "relatedServiceSlugs" | "relatedAreaSlugs"
  >
>;

export type ServiceAreaContentTranslation = {
  intro: string;
  localContext: string;
  assessmentFocus: readonly string[];
  propertyContexts: readonly string[];
  faqs: readonly { question: string; answer: string }[];
};

export const services = {
  "roof-replacement": {
    title: "استبدال السقف",
    shortTitle: "استبدال",
    eyebrow: "تخطيط السقف الكامل",
    description:
      "مسار منضبط من تقييم الحالة إلى نظام سقف كامل، بما يتناسب مع المنزل وهندسة السقف ومناخ مترو فانكوفر.",
    definition:
      "استبدال ألواح الشينجل الأسفلتية هو إعادة تنفيذ كاملة للسقف المائل: تُزال الطبقة الحالية، وتُعالج الركيزة والطبقة السفلية، ثم يُركّب نظام شينجل جديد مع إعادة دمج الفلاشينغ والفتحات ضمن نظام واحد.",
    forWhom:
      "استبدال السقف للمنازل التي تعاني من تدهور واسع في الغطاء، أو أعطال متكررة، أو نظام سقف قرب نهاية عمره، أو مشروع تجديد يتطلب إعادة تسقيف بشكل أكثر اتساقاً.",
    warningSigns: [
      "تدهور عبر عدة أسطح سقف",
      "تسرب متكرر في مناطق متعددة",
      "تجعد أو تشقق أو فقدان أو تآكل واسع للألواح",
      "أدلة رطوبة مستمرة في العلية أو السقف",
      "عدة انتقالات أو اختراقات أو ترقيعات ضعيفة",
    ],
    evaluates: [
      "حالة الغطاء والركيزة الحالية",
      "المنحدرات والوديان والانتقالات ومسارات الصرف",
      "الفلاشينغ والاختراقات والحواف وواجهات الجدران",
      "التهوية وأدلة الرطوبة المرئية",
      "الوصول والحماية ومتطلبات التخلص",
    ],
    causes: [
      "التقادم الطبيعي ووصول السقف إلى نهاية عمره الافتراضي",
      "الرطوبة المزمنة ونمو الطحالب",
      "تراكم أضرار الرياح بمرور الوقت",
      "ضعف التهوية وتسارع تلف المواد",
      "قصور التفاصيل في أعمال تركيب سابقة",
    ],
    methodology: [
      "توثيق حالة السقف والخيارات المتاحة",
      "وضع خطة الإزالة وحماية العقار والتخلص من المخلفات",
      "إصلاح سطح التثبيت وفق ما تكشفه الإزالة والمواصفات المتفق عليها",
      "تركيب الطبقة السفلية والفلاشينغ وألواح الشينجل بالتسلسل الصحيح",
      "تنفيذ تفاصيل القمم والأودية والحواف والفتحات باعتبارها نظاماً متكاملاً",
    ],
    materials: [
      "ألواح شينجل أسفلتية معمارية",
      "طبقة سفلية وحماية من الجليد والماء عند الحاجة",
      "فلاشينغ للأودية والجدران والفتحات",
      "ملحقات القمم والتهوية",
      "مثبّتات وقطع حواف متوافقة مع النظام",
    ],
    bcClimate:
      "تجعل الأمطار الساحلية ونمو الطحالب جودة الطبقة السفلية وتفاصيل الأودية والتهوية عوامل أساسية في أداء أسقف الشينجل في لوور مينلاند.",
    whenAppropriate:
      "عندما يكون تآكل الشينجل واسعاً في نظام السقف أو تترك الإصلاحات المتكررة أعطالاً أخرى متوقعة في المدى القريب.",
    whenOtherService:
      "قد يكون الإصلاح الموضعي أنسب عندما يقتصر الضرر الناتج من الرياح على مساحة محددة وتبقى بقية طبقة السقف بحالة جيدة.",
    solutionCategories: [
      {
        title: "ألواح إسفلتية معمارية",
        body: "حل سكني شائع للأسقف شديدة الانحدار المناسبة، بملفات وألوان متنوعة.",
      },
      {
        title: "سقف معدني بدرز واقف",
        body: "خيار معماري متميز للأشكال المناسبة، مع تثبيت مخفي وخطوط نظيفة.",
      },
      {
        title: "نظام سقف أغشية منخفضة الميل",
        body: "فئة نظام للأجزاء المسطحة ومنخفضة الميل حيث تفاصيل الصرف والانتقال حاسمة.",
      },
    ],
    repairVsReplace: {
      repair: "قد يكون الإصلاح منطقياً عندما يمكن خدمة نظام السقف المحيط وعزل العطل والوصول إليه.",
      replace:
        "قد يكون الاستبدال منطقياً عندما يكون التدهور واسعاً أو تتكرر الأعطال أو لا يحل إصلاح جزء واحد مخاطر قصيرة أخرى.",
    },
    scopeFactors: [
      "مساحة وهندسة السقف",
      "الطبقات الحالية وحالة الركيزة",
      "اختيار المواد ونظام السقف",
      "تعقيد الفلاشينغ والاختراقات",
      "وصول الموقع والحماية",
      "التصاريح والمتطلبات المحلية",
    ],
    process: [
      "تقييم الغطاء ومؤشرات الركيزة والتهوية",
      "نطاق عمل مكتوب بخيارات المواد والتفاصيل",
      "إزالة مع حماية العقار والتخلص",
      "إصلاح الركيزة والطبقة السفلية والفلاشينغ وتركيب الألواح بالتسلسل",
      "جولة نهائية ومراجعة الإنجاز",
    ],
    publicPriceHint:
      "يعتمد سعر المشروع على مساحة السقف والميل وعدد الطبقات وإصلاحات الركيزة وتعقيد الوديان والنوافذ السقفية واختيار المواد. اطلب تقييماً للحصول على نطاق عمل مكتوب.",
    faqs: [
      {
        question: "ما هو استبدال الألواح الإسفلتية؟",
        answer:
          "استبدال الألواح الإسفلتية هو إعادة تسقيف كاملة لسقف شديد الانحدار تزيل الغطاء الحالي وتعالج احتياجات الركيزة والطبقة السفلية وتركب نظام ألواح جديداً مع إعادة دمج الفلاشينغ والاختراقات.",
      },
      {
        question: "كم يستغرق استبدال الألواح الإسفلتية؟",
        answer:
          "معظم إعادة التسقيف للمنازل العائلية تُقاس بالأيام لا بالأسابيع، لكن المدة تعتمد على حجم السقف والميل ونوافذ الطقس وكمية إصلاح الركيزة المكتشفة أثناء الإزالة. يحدد نطاق العمل المكتوب التوقعات قبل بدء العمل.",
      },
      {
        question: "هل يمكن تركيب ألواح جديدة فوق القديمة؟",
        answer:
          "التغطية فوق القديم ممكنة تقنياً أحياناً، لكن الإزالة الكاملة مفضلة عادة: تسمح بفحص الركيزة والطبقة السفلية الصحيحة ودمج الفلاشينغ، وتجنب تراكم الوزن وحبس المشاكل تحت الغطاء الجديد.",
      },
      {
        question: "ماذا يحدث إذا وُجدت ركيزة متآكلة أثناء الإزالة؟",
        answer:
          "يُوثق ضرر الركيزة المكتشف ويُصلح قبل تركيب النظام الجديد. يشرح النطاق الواضح مسبقاً كيف يُقاس عمل الركيزة ويُسعّر، حتى لا تكون الاكتشافات أثناء المشروع مفاجآت.",
      },
      {
        question: "أي ألواح تتحمل أفضل طقس مترو فانكوفر؟",
        answer:
          "الألواح المعمارية الطبقية هي الخيار السكني الشائع محلياً. في هذا المناخ، جودة الطبقة السفلية وتفاصيل الوديان والحواف وتهوية العلية تؤثر على العمر الفعلي بقدر منتج اللوح نفسه.",
      },
    ],
  },
  "roof-repair": {
    title: "إصلاح السقف وتشخيص التسرب",
    shortTitle: "إصلاح السقف",
    eyebrow: "تحديد مصدر التسرب",
    description:
      "تحقيق مركز وخطة إصلاح للتسرب والأعطال الموضعية والاختراقات وعيوب الفلاشينغ والأضرار القابلة للإصلاح.",
    definition:
      "إصلاح تسرّب السقف الطارئ هو فحص مركّز واحتواء لدخول الماء النشط أو المتزايد عبر غطاء السقف أو الفلاشينغ أو الفتحات ونقاط الاتصال. وقد يشمل عزلاً مائياً مؤقتاً أو إصلاحاً موضعياً دائماً عندما تسمح الظروف.",
    forWhom:
      "إصلاح السقف لأصحاب المنازل الذين يعانون من تسرب نشط أو متقطع، أو ضرر موضعي واضح، أو ألواح منزوعة، أو اختراق مشبوه، أو منطقة تحتاج تقييماً متخصصاً.",
    warningSigns: [
      "بقع سقف أو تقاطر أو رطوبة في العلية",
      "ألواح أو ألواح معدنية مفقودة أو مرتفعة أو تالفة",
      "فشل في المادة المانعة للتسرّب أو انفصال فلاشينغ مرئي",
      "تجمع ماء قرب المزاريب أو الانتقالات",
      "تسرب يعود بعد ترقيع سطحي",
    ],
    evaluates: [
      "الأدلة الداخلية ومسارات الماء المحتملة",
      "الغطاء حول المنطقة المتأثرة",
      "الفلاشينغ والاختراقات والوديان والانتقالات",
      "حالة التصريف وأماكن تراكم الحطام",
      "هل تدعم المواد المجاورة إصلاحاً دائماً",
    ],
    causes: [
      "تلف الفلاشينغ أو أختام فتحات الأنابيب",
      "اقتلاع الرياح لبعض مواد الغطاء أو رفعها",
      "انسداد التصريف بالحطام وتجمع الماء في نقطة واحدة",
      "تضرر القمم أو الحواف أو نقاط الانتقال بعد العواصف",
      "إصلاحات سابقة عالجت الأثر ولم تعالج مصدر التسرّب",
    ],
    methodology: [
      "توثيق المؤشرات الداخلية والخارجية بأمان",
      "تتبّع مسار دخول الماء الأكثر احتمالاً، لا موضع البقعة فقط",
      "تنفيذ عزل مائي مؤقت عندما يفرض الطقس أو الوصول ذلك",
      "إجراء إصلاح موضعي متوافق عندما تكون الأجزاء المحيطة قابلة للصيانة",
      "شرح المخاطر المتبقية وما إذا كانت هناك حاجة إلى عمل أوسع",
    ],
    materials: [
      "مواد غطاء متوافقة مع نظام السقف الحالي",
      "رقع للطبقة السفلية أو الغشاء عند ملاءمتها",
      "قطع فلاشينغ وأغطية فتحات الأنابيب",
      "مواد مانعة للتسرّب بوصفها جزءاً من تفصيل صحيح، لا حلاً منفرداً",
      "أغطية أو مواد عزل مائي مؤقت عند الحاجة",
    ],
    bcClimate:
      "تعني كثرة الأمطار في مترو فانكوفر أن خللاً صغيراً قد يسبب أضراراً داخلية متكررة. لذلك يظل تشخيص المصدر أهم من رقعة سطحية لا تعالج مسار الماء الحقيقي.",
    whenAppropriate:
      "عندما يكون دخول الماء موضعياً وتبقى الأجزاء المحيطة قابلة للصيانة ويمكن تنفيذ إصلاح دائم أو عزل مؤقت بأمان.",
    whenOtherService:
      "قد يستدعي تلف الغطاء على نطاق واسع أو تعدد مصادر التسرّب أو بلوغ النظام نهاية عمره تخطيط إصلاح أوسع أو حماية مؤقتة بعد العاصفة أو مناقشة الاستبدال الكامل.",
    solutionCategories: [
      {
        title: "إصلاح موضعي للغطاء",
        body: "استبدال أو تأمين مناطق محكمة عندما تكون المواد المحيطة متوافقة وصالحة.",
      },
      {
        title: "عمل الفلاشينغ والاختراقات",
        body: "تصحيح حول الجدران والفتحات والمداخن وواجهات أخرى يدخل منها الماء غالباً.",
      },
      {
        title: "تصحيح الصرف والانتقال",
        body: "يهدف إلى إبقاء الماء أو توجيهه أو تركيزه عبر الهندسة والتفاصيل.",
      },
    ],
    repairVsReplace: {
      repair: "عادةً يستحق الإصلاح النظر عندما يُحدد المصدر ويقبل النظام المحيط عملاً متوافقاً.",
      replace:
        "إذا أظهر التحقيق تدهوراً واسعاً أو عدة نقاط عطل مستقلة، يصبح النقاش حول الاستبدال أكثر اتساقاً.",
    },
    scopeFactors: [
      "الوصول إلى منطقة المصدر",
      "توافق المواد الحالية",
      "مدى الضرر المخفي",
      "تعقيد الفلاشينغ أو الاختراق",
      "نافذة الطقس والحماية المؤقتة",
      "الحاجة لفتح للتحقق من الحالة",
    ],
    process: [
      "اتصل أو أرسل تفاصيل وصوراً لما تراه",
      "فرز وجدولة: حضور في نفس اليوم عند توفر الموعد",
      "تشخيص المصدر على السقف مع توثيق داخلي وخارجي",
      "عزل مائي مؤقت أو إصلاح موضعي دائم حسب الظروف",
      "ملخص مكتوب للنتائج والعمل المنجز والخطوات التالية الموصى بها",
    ],
    publicPriceHint:
      "يعتمد نطاق الطوارئ على الوصول والطقس أثناء العمل ومدى انتشار الماء وما إذا كانت حماية مؤقتة مطلوبة قبل الإصلاح الدائم. اطلب تقييماً لتسعير خاص بالمشروع.",
    faqs: [
      {
        question: "ما هو إصلاح طوارئ تسرب السقف؟",
        answer:
          "إصلاح طوارئ تسرب السقف هو تحقيق مركز وتثبيت لدخول الماء النشط أو المتسارع عبر الغطاء أو الفلاشينغ أو الاختراق أو الواجهة ذات الصلة. قد يشمل عزلاً مائياً مؤقتاً وإصلاحاً موضعياً دائماً عندما تسمح الظروف.",
      },
      {
        question: "هل يمكن إصلاح التسرب أثناء المطر؟",
        answer:
          "الخطوة الأولى الآمنة في المطر النشط عادة عزل مائي مؤقت: تغطية أو إغلاق منطقة الدخول، لأن معظم مواد التسقيف الدائمة تحتاج ظروفاً جافة للالتصاق. يُكمل الإصلاح الدائم في نافذة طقس مناسبة.",
      },
      {
        question: "لماذا بقعة السقف بعيدة عن أي ضرر ظاهر على السقف؟",
        answer:
          "غالباً يدخل الماء عند تفصيل واحد ويسافر على الطبقة السفلية أو الهيكل أو الركيزة قبل الظهور داخلياً. لذلك يتتبع التشخيص مسار الدخول المحتمل عبر السقف لا يُرقّع مباشرة فوق البقعة.",
      },
      {
        question: "هل تقدمون إصلاح تسرب في نفس اليوم في مترو فانكوفر؟",
        answer:
          "نسعى إلى معاينة التسرّبات النشطة في اليوم نفسه عندما يسمح جدول المواعيد وساعات النهار، لكن لا يمكن ضمان وقت الحضور. ويساعد إرسال صور ووصف واضح لما تراه على تقييم أولوية الطلب بدقة.",
      },
      {
        question: "هل سيكون إصلاح التسرب مؤقتاً أم دائماً؟",
        answer:
          "يعتمد على الطقس وتوفر المواد وما يكشفه التحقيق. عندما يمكن تنفيذ إصلاح موضعي دائم بأمان في الزيارة الأولى، يُنفذ. وإلا يُثبت السقف بعزل مائي مؤقت ويُحدد العمل الدائم كتابياً.",
      },
      {
        question: "كم يكلف إصلاح طوارئ تسرب السقف؟",
        answer:
          "تعتمد التكلفة على ارتفاع السقف وسهولة الوصول وحجم الضرر المخفي والحاجة إلى حماية مؤقتة إلى جانب الإصلاح الأساسي. وتقدم رولين سعراً مخصصاً للمشروع بعد التقييم، ولا تعتمد سعراً نهائياً من دون معاينة.",
      },
    ],
  },
  "asphalt-shingle-roofing": {
    title: "تسقيف بألواح إسفلتية معمارية",
    shortTitle: "ألواح إسفلتية",
    eyebrow: "نظام شديد الانحدار",
    description:
      "فئة سقف عملية مع مراعاة التصميم لكثير من منازل مترو فانكوفر ذات أنظمة سقف مناسبة شديدة الانحدار.",
    forWhom:
      "الألواح الإسفلتية المعمارية مناسبة لكثير من الأسقف السكنية المائلة حيث يريد المالك نظاماً مألوفاً وخيارات جمالية واسعة وإمكانية خدمة بسيطة لاحقاً.",
    warningSigns: [
      "فقدان الحبيبات أو ظهور الطبقة السفلية",
      "تجعد أو تشقق أو انزياح الألواح",
      "ضرر حول الوديان أو الحواف أو الاختراقات",
      "نمو الطحالب على سطح السقف",
      "إصلاحات متكررة عبر عدة أسطح",
    ],
    evaluates: [
      "ميل وهندسة السقف",
      "حالة السطح بعد تحقيق مناسب",
      "الطبقة السفلية والأفاريز والوصول للوديان",
      "التهوية ومسارات الهواء",
      "الفلاشينغ والصرف",
    ],
    solutionCategories: [
      {
        title: "ألواح معمارية طبقية",
        body: "ملف أبعاد شائع للأسقف السكنية شديدة الانحدار يُختار حسب نظام السقف ومتطلبات التصميم.",
      },
      {
        title: "الطبقة السفلية وتفاصيل تصريف الماء",
        body: "الألواح المرئية طبقة واحدة من نظام سقف منسق يشمل التغليف والوديان والحواف والانتقالات.",
      },
      {
        title: "دمج الفلاشينغ",
        body: "واجهات الجدران والمداخن وفتحات الأنابيب وعناصر السقف تحتاج ترتيباً متعمداً مع سطح الألواح.",
      },
    ],
    repairVsReplace: {
      repair:
        "قد يكون الإصلاح الموضعي عملياً إذا وُجدت ألواح مطابقة وتندمج ألواح متوافقة دون إتلاف السطح الهش المحيط.",
      replace:
        "إذا كانت الألواح هشة أو مهترئة أو متضررة على نطاق واسع عبر عدة أسطح، قد يكون الاستبدال أنسب.",
    },
    scopeFactors: [
      "ميل ومساحة السقف الكلية",
      "عدد الوديان والنوافذ السقفية والاختراقات",
      "الطبقات الحالية وإصلاح السطح",
      "ملف الألواح والملحقات",
      "التهوية وعمل الفلاشينغ",
      "الوصول وحماية الموقع",
    ],
    faqs: [
      {
        question: "هل الألواح المعمارية مناسبة لكل سقف مائل؟",
        answer:
          "لا. يجب مراعاة الميل والركيزة والتعرض والشكل والمتطلبات المحلية. يُقيَّم السقف كنظام سقف قبل اختيار المنتج.",
      },
      {
        question: "هل يؤثر اللون على النتيجة المعمارية؟",
        answer:
          "نعم. لون السقف يتفاعل مع التكسية والإطار والكتلة والرؤية من الشارع. يجب مقارنة العينات بالمبنى الفعلي لا الشاشة فقط.",
      },
    ],
  },
  "metal-roofing": {
    title: "تسقيف بسقف معدني بدرز واقف",
    shortTitle: "تسقيف معدني",
    eyebrow: "سقف معماري متميز",
    description: "خيار سقف معدني أنيق للعمارة المتوافقة وشكل السقف وأولويات الأداء.",
    definition:
      "يشمل استبدال السقف المعدني تركيب نظام معدني جديد، غالباً بدرزات قائمة، بعد تقييم شكل السقف والركيزة والطبقة السفلية والنتيجة المعمارية المطلوبة.",
    forWhom:
      "السقف المعدني بدرز واقف لأصحاب يقدّرون خطوطاً معمارية نظيفة وتثبيتاً مخفياً وتعبيراً مادياً متميزاً في تصميم السقف المناسب.",
    warningSigns: [
      "ألواح مرتخية أو تالفة أو ملتوية",
      "تآكل أو تلف في الطلاء",
      "درز مفتوح أو حافة ضعيفة أو فشل في التشطيب",
      "أدلة ماء قرب الاختراق والانتقال",
      "حركة أو ضوضاء تحتاج تقييم النظام",
    ],
    evaluates: [
      "ميل السقف وطول السطح وتخطيط الألواح",
      "الركيزة ومتطلبات الطبقة السفلية",
      "التمدد الحراري وطريقة التثبيت",
      "الثلج والرياح والصرف في الموقع",
      "الحواف والاختراق والانتقال لمواد أخرى",
    ],
    causes: [
      "ترقية مخططة لنظام السقف",
      "وصول السقف الحالي إلى نهاية عمره الافتراضي",
      "تجديد التصميم المعماري",
      "أهداف أداء تتوافق مع خصائص أنظمة السقف المعدني",
    ],
    methodology: [
      "التأكد من ملاءمة شكل السقف للنظام المعدني",
      "تحديد نوع الألواح والطبقة السفلية",
      "تهيئة سطح التثبيت",
      "تركيب الألواح والفلاشينغ كنظام متكامل",
      "تنفيذ تفاصيل القمم والحواف والفتحات بعناية",
    ],
    materials: [
      "ألواح بدرزات قائمة أو ألواح معدنية محددة للمشروع",
      "طبقات سفلية متوافقة مع النظام",
      "مشابك ومثبّتات وفق مواصفات النظام",
      "فلاشينغ وقطع إغلاق محكمة",
    ],
    bcClimate:
      "تنسجم الأسقف المعدنية ذات الدرزات القائمة مع كثير من طرز الساحل الغربي، لكن الطبقة السفلية والتهوية وجودة التفاصيل هي التي تحدد الأداء الفعلي في الطقس الماطر.",
    whenAppropriate:
      "عندما يكون المعدن هو النظام المختار ويشمل نطاق المشروع استبدال السقف بالكامل.",
    whenOtherService:
      "قد يكون إصلاح المكونات المتضررة كافياً إذا كان نظام السقف المعدني الحالي سليماً في بقية أجزائه.",
    solutionCategories: [
      {
        title: "ألواح بدرز واقف",
        body: "ألواح متصلة بتثبيت مخفي ودرز بارز موصوفة كجزء من نظام السقف الكامل.",
      },
      {
        title: "تفاصيل الحافة المعمارية",
        body: "الأفاريز والمزاريب والحافة العلوية وواجهات الجدران تحدد أداء السقف المكتمل ونظافته.",
      },
      {
        title: "تنسيق السقف المختلط",
        body: "المنازل التي تجمع المعدن والغشاء أو الألواح تحتاج صرفاً وانتقالات مواد متناسقة.",
      },
    ],
    repairVsReplace: {
      repair:
        "قد يكون الإصلاح الانتقائي ممكناً عند الوصول للمكونات المتأثرة ودمجها مع نظام الألواح الحالي.",
      replace:
        "إذا تضرر تخطيط الألواح أو التثبيت أو الطبقة السفلية أو عدة انتقالات بشكل منهجي، قد يلزم استبدال أوسع.",
    },
    scopeFactors: [
      "شكل الألواح وتخطيط الدرز",
      "اختيار المعدن والملف والتشطيب",
      "تحضير الركيزة والطبقة السفلية",
      "متطلبات فلاشينغ مخصص",
      "تنسيق الاختراق والثلج والصرف",
      "الوصول ومناولة المواد",
    ],
    process: [
      "تأكيد ملاءمة الهندسة والنظام",
      "تحديد ملف اللوح والطبقة السفلية والتفاصيل",
      "نطاق عمل مكتوب مع توثيق قرارات التصميم",
      "تحضير الركيزة وتركيب الألواح",
      "مراجعة التفاصيل وجولة إنجاز",
    ],
    publicPriceHint:
      "يعتمد سعر إعادة التسقيف المعدني على هندسة السقف وملف اللوح واستراتيجية الطبقة السفلية وتعقيد الاختراقات. اطلب تقييماً للحصول على نطاق عمل مكتوب.",
    faqs: [
      {
        question: "ما هو استبدال السقف المعدني؟",
        answer:
          "استبدال السقف المعدني يركب نظام تسقيف معدني جديد، غالباً بدرز واقف، بعد تقييم الهندسة والركيزة والطبقة السفلية والنية المعمارية.",
      },
      {
        question: "هل السقف المعدني صاخب في المطر؟",
        answer:
          "في إعادة تسقيف سكنية فوق ركيزة صلبة مع طبقة سفلية وعزل تحتها، ضوضاء المطر أقل بكثير مما يتوقع معظم الناس: الارتباط بالصوت العالي يأتي من ألواح فوق هيكل مكشوف. تُناقش التوقعات الصوتية أثناء اختيار النظام.",
      },
      {
        question: "بدرز واقف مقابل معدن بمسامير ظاهرة: ما الفرق؟",
        answer:
          "البدرز الواقف يخفي التثبيت على مشابك تحت الدرزات البارزة، فيسمح بحركة اللوح ويبقي المسامير المطاطية بعيداً عن الطقس. الألواح بمسامير ظاهرة أقل تكلفة لكن تحتاج صيانة مسامير طويلة الأمد. للعمل السكني المتميز، البدرز الواقف عادة التوصية.",
      },
      {
        question: "هل يمكن تركيب معدن فوق الألواح الحالية؟",
        answer:
          "ممكن إنشائياً أحياناً، لكن الإزالة الكاملة مفضلة عادة لفحص الركيزة وبناء الطبقة السفلية ونظام الفلاشينغ بشكل صحيح. حبس غطاء قديم يعقد التشخيص لاحقاً.",
      },
      {
        question: "هل تعمل الأسقف المعدنية على أشكال سقف معقدة؟",
        answer:
          "نعم، لكن التعقيد حيث تُربح أو تُخسر مشاريع المعدن: الورك والوديان والنوافذ السقفية والتفاصيل الميتة تحتاج حرفية معدنية وتزيد التكلفة. مراجعة الهندسة هي الخطوة الأولى لتحديد ملاءمة المعدن للمنزل.",
      },
    ],
  },
  "flat-roofing": {
    title: "الأسقف المسطحة ومنخفضة الميل",
    shortTitle: "سقف مسطح",
    eyebrow: "نظام سقف أغشية",
    description:
      "تسقيف للمناطق المسطحة ومنخفضة الميل حيث استمرارية الغشاء وميل الصرف والانتقالات الدقيقة تحدد الأداء.",
    definition:
      "يتضمن استبدال السقف المسطح إزالة نظام السقف منخفض الميل أو تغطيته وفق المواصفات، ثم تركيب غشاء جديد يُختار بما يتناسب مع التصريف والركيزة ومتطلبات المشروع.",
    forWhom:
      "هذه الخدمة للمنازل منخفضة الميل والإضافات وأجزاء السقف التي تحتاج نظام سقف غشاء مناسباً لا غطاءً شديد الانحدار.",
    warningSigns: [
      "تجمع ماء أو صرف بطيء بعد المطر",
      "درز مفتوح أو انتفاخ أو تلف سطحي",
      "رطوبة قرب المزاريب أو الدرابزين أو حافة السقف",
      "تفاصيل انتقال مكسورة أو فاشلة",
      "ترقيع متكرر في نفس المكان",
    ],
    evaluates: [
      "الميل ومسار الصرف",
      "سطح الغشاء والدرزات والنهايات",
      "المزاريب والمصارف ومضادات الفيض",
      "الدرابزين والحافة والاختراق والانتقال",
      "مشاكل الركيزة أو الرطوبة المرئية",
    ],
    causes: [
      "وصول الغشاء إلى نهاية عمره الافتراضي",
      "تشبّع طبقات العزل بالرطوبة",
      "قيود أو عيوب في تصميم التصريف",
      "التقادم الناتج من الأشعة فوق البنفسجية وتغيرات الحرارة",
      "كثرة الفتحات وتراكم الأعطال في تفاصيلها",
    ],
    methodology: [
      "تقييم طبقات السقف، بما فيها الأجزاء الرطبة عندما يستدعي الفحص ذلك",
      "اختيار نظام الغشاء المناسب للمشروع",
      "إزالة النظام القائم أو تغطيته وفق المواصفات",
      "تركيب العزل وطبقات الغشاء بالتسلسل الصحيح",
      "تنفيذ تفاصيل المصارف والحواجز والفتحات كنظام متكامل",
    ],
    materials: [
      "أغشية SBS أو TPO أو PVC أو نظام آخر يحدده المشروع",
      "طبقات عزل وألواح تغطية بحسب نطاق العمل",
      "ملحقات المصارف والفلاشينغ",
      "مواد لاصقة أو مثبّتات أو لحام حراري وفق متطلبات النظام",
    ],
    bcClimate:
      "تعتمد الأسقف منخفضة الميل في مناخ مترو فانكوفر الماطر على تصميم تصريف جيد وتفاصيل غشاء متينة أكثر من اعتمادها على الاسم التجاري للنظام.",
    whenAppropriate:
      "عندما تكون طبقات السقف منخفض الميل قد استُهلكت أو يجري تجديدها ضمن مشروع أوسع.",
    whenOtherService:
      "قد يظل الإصلاح الموضعي للغشاء خياراً مناسباً إذا كانت بقية طبقات النظام بحالة جيدة.",
    solutionCategories: [
      {
        title: "نظام غشاء أحادي الطبقة",
        body: "فئة نظام السقف منخفض الميل مختارة وموصوفة لركيزة وشكل ومتطلبات المشروع.",
      },
      {
        title: "نظام غشاء متعدد الطبقات",
        body: "نظام سقف طبقي حيث يتطابق التكوين والتفاصيل مع ظروف المشروع.",
      },
      {
        title: "عمل الصرف والانتقال",
        body: "تصحيح أو تجديد حول حيث يخرج الماء من السقف أو يلتقي الغشاء بالجدار والحافة وسقف آخر.",
      },
    ],
    repairVsReplace: {
      repair:
        "قد يكون الإصلاح منطقياً عندما يكون الغشاء قابلاً للخدمة والعيب معزولاً في مواد سليمة.",
      replace:
        "عندما يظهر عيب الدرز أو سطح الغشاء أو الصرف في السقف كله لا جزءاً واحداً، يزداد احتمال التجديد.",
    },
    scopeFactors: [
      "مساحة وهندسة الصرف",
      "الغشاء والركيزة الحالية",
      "عدد المزاريب والحواف والدرابزين",
      "حاجة العزل أو تصحيح الميل",
      "الوصول ومعدات السطح",
      "متطلبات المبنى والبلدية",
    ],
    process: [
      "تقييم نظام السقف بما في ذلك تحقيق الرطوبة عند الحاجة",
      "اختيار نظام الغشاء ونطاق عمل مكتوب",
      "إزالة أو تغطية نظام السقف الحالي حسب المواصفة",
      "تركيب العزل والغشاء بالتسلسل",
      "مراجعة تفاصيل المصارف والدرابزين والاختراقات",
    ],
    publicPriceHint:
      "يعتمد سعر استبدال المنحدر المنخفض على المساحة واختيار الغشاء واحتياجات العزل وعمل الصرف وكثافة الاختراقات. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو استبدال السقف المسطح بـ SBS أو TPO أو PVC؟",
        answer:
          "استبدال السقف المسطح يزيل أو يغطي نظام سقف منخفض الميل ويركب نظام غشاء جديد يُختار للصرف والركيزة ومتطلبات المشروع.",
      },
      {
        question: "SBS أو TPO أو PVC: أي غشاء مسطح أفضل؟",
        answer:
          "لا يوجد أفضل عالمياً. SBS ثنائي الطبقة هو العمود الفقري الإقليمي بمرونة قوية؛ TPO و PVC أنظمة أحادية الطبقة بدرزات ملحومة حرارياً ووزن أخف وخيارات عاكسة، و PVC يضيف مقاومة كيميائية قوية. الركيزة والصرف واستخدام السطح والميزانية تحدد.",
      },
      {
        question: "هل نزيل نظام السقف الحالي أم نغطيه؟",
        answer:
          "التغطية قد توفر تكلفة وتخلصاً عندما يكون نظام السقف جافاً وسليماً، لكن العزل الرطب أو طبقات متعددة تجعل الإزالة المسار الصادق. تحقيق الرطوبة قبل القرار يحميك من حبس المشاكل.",
      },
      {
        question: "ما مدى إزعاج استبدال السقف المسطح؟",
        answer:
          "توقع ضوضاء وأبخرة أثناء عمل اللهب أو اللاصق وتحميل مواد على مراحل ومسارات وصول محمية. عادة يمكن البقاء في المنزل، ويحدد نطاق العمل المكتوب التسلسل وساعات العمل وإجراءات الحماية مسبقاً.",
      },
      {
        question: "هل يمكن تحسين الصرف أثناء الاستبدال؟",
        answer:
          "الاستبدال هو اللحظة المناسبة لتصحيح تجمع الماء المزمن: عزل مائل ومصارف إضافية أو منقولة وعمل فتحات تصريف يمكن تصميمها في نظام السقف الجديد بدلاً من محاربتها لاحقاً.",
      },
    ],
  },
  "roof-inspection-maintenance": {
    title: "فحص السقف والصيانة الوقائية",
    shortTitle: "فحص وصيانة",
    eyebrow: "اعرف الحالة",
    description:
      "توثيق نظام السقف والتفاصيل الضعيفة مع أولويات عملية للصيانة أو المراقبة أو العمل التالي.",
    forWhom:
      "الفحص والصيانة الوقائية لأصحاب يريدون التخطيط مبكراً أو مراجعة المخاوف أو الاستعداد لقرار تجديد أو سجل أوضح لحالة السقف المرئية.",
    warningSigns: [
      "لا سجل حديث لحالة السقف",
      "حطام أو طحالب يؤثر على الصرف",
      "مادة مانعة للتسرّب أو فلاشينغ أو اختراق يظهر تآكلاً",
      "رطوبة داخلية بمصدر غير معروف",
      "عمل خارجي مخطط يتقاطع مع السقف",
    ],
    evaluates: [
      "حالة الغطاء أو الغشاء",
      "الفلاشينغ والاختراقات ووصلات المادة المانعة للتسرّب",
      "المزاريب والمصارف ومسار الصرف المرئي",
      "الحواف والانتقالات وعناصر السطح",
      "أدلة داخلية أو علية عند الصلة والإمكان",
    ],
    solutionCategories: [
      {
        title: "توثيق الحالة",
        body: "تسجيل الملاحظات والمناطق للصيانة أو المراقبة أو تحقيق أكثر تدخلاً.",
      },
      {
        title: "نطاق صيانة وقائية",
        body: "أعمال صغيرة ذات أولوية للتفاصيل القابلة للخدمة قبل فشل أكبر.",
      },
      {
        title: "تخطيط الإصلاح أو الاستبدال",
        body: "وضّح الخطوة التالية عندما لا تحل الصيانة وحدها المشكلة وفق الحالة الملحوظة.",
      },
    ],
    repairVsReplace: {
      repair:
        "يمكن للفحص تحديد عناصر صيانة وإصلاح معزولة مع إبقاء نظام السقف الأوسع قابلاً للخدمة.",
      replace:
        "إذا كانت الحالة ضعيفة باستمرار عبر نظام السقف، قد يدعم التقرير تخطيط نطاق أكثر شمولاً.",
    },
    scopeFactors: [
      "حجم وإمكانية الوصول للسقف",
      "نظام السقف وعدد المستويات",
      "عمق التوثيق المطلوب",
      "حاجة لمراجعة داخلية أو علية",
      "حاجة لفتح أو إشراك متخصص",
      "طلبات صيانة لاحقة",
    ],
    process: [
      "حجز فحص صحة ووصف أي مخاوف",
      "فحص خارجي منهجي مع توثيق بالصور",
      "إنجاز بنود صيانة بسيطة متفق عليها أثناء الزيارة",
      "ملخص حالة مكتوب بأولويات مرتبة",
      "مناقشة متابعة لأي إصلاحات موصى بها",
    ],
    publicPriceHint:
      "يعتمد نطاق فحص الصحة على حجم السقف وتعقيده والوصول وكمية التوثيق والصيانة البسيطة المشمولة. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو فحص صحة السقف والصيانة؟",
        answer:
          "فحص صحة السقف هو تقييم بصري منظم للغطاء والفلاشينغ والصرف والمكونات ذات الصلة المتاحة، عادة مع توثيق بالصور وبنود صيانة محدودة متفق عليها مسبقاً.",
      },
      {
        question: "كم مرة يجب فحص السقف؟",
        answer:
          "إيقاع محلي مفيد هو فحص قبل أمطار الخريف، وبعد أي عاصفة رياح كبيرة. المنازل تحت غطاء شجري كثيف، أو بأسقف في النصف الثاني من عمرها المتوقع، تستفيد من اهتمام أكثر تكراراً.",
      },
      {
        question: "ماذا أستلم بعد فحص صحة السقف؟",
        answer:
          "ملخص موثق بالصور لحالة الغطاء والفلاشينغ والصرف والتهوية، مفصول إلى بنود تحتاج إصلاحاً عاجلاً وبنود للمراقبة وبلي طبيعي. وثيقة تخطيط يمكنك التصرف بناءً عليها بوتيرتك.",
      },
      {
        question: "هل يستحق الفحص قبل شراء أو بيع منزل؟",
        answer:
          "نعم: السقف من أغلى مكونات المبنى تجديداً، وخط أساس موثق للحالة يقلل تخمين التفاوض. لاحظ أن هذا مراجعة حالة تسقيف لا فحص منزل رسمي قبل الشراء.",
      },
      {
        question: "هل تصلحون المشاكل المكتشفة أثناء الفحص؟",
        answer:
          "بنود صيانة بسيطة متفق عليها، مثل تجديد مادة مانعة للتسرّب بسيط عند الاختراقات، يمكن إنجازها أثناء الزيارة. النتائج الأكبر تُوثق وتُحدد نطاقها بشكل منفصل لتقرر بمعلومات واضحة لا ضغط فوري.",
      },
    ],
  },
  "flashing-roof-penetration-repair": {
    title: "إصلاح الفلاشينغ واختراقات السقف",
    shortTitle: "فلاشينغ واختراقات",
    eyebrow: "الانتقالات والواجهات",
    description:
      "إصلاح فلاشينغ المداخن والجدران والفتحات وأحذية الأنابيب واختراقات السقف الأخرى حيث يدخل الماء غالباً في أسقف مترو فانكوفر.",
    definition:
      "إصلاح الفلاشينغ والاختراقات يصحح واجهات تصريف الماء حيث يلتقي الغطاء بالجدران والمداخن والفتحات والأنابيب. هذه التفاصيل مصادر تسرب شائعة عند فشل المعادن أو الأحذية أو وصلات المادة المانعة للتسرّب.",
    metaTitle: "إصلاح الفلاشينغ والاختراقات فانكوفر | رولين روفينغ",
    metaDescription:
      "إصلاح فلاشينغ المداخن والجدران والفتحات وأحذية الأنابيب لأسقف مترو فانكوفر. تقييم يركز على المصدر وإعادة تفصيل متوافقة للاختراقات.",
    forWhom:
      "أصحاب منازل يلاحظون تسربات قرب المداخن أو الجدران الجانبية أو أكوام الصرف أو فتحات التهوية الميكانيكية أو فتحات السقف الأخرى، أو لديهم فلاشينغ متقادم لا يصرف الماء بشكل صحيح.",
    warningSigns: [
      "بقع قرب مداخل المداخن أو الجدران الخارجية",
      "مادة مانعة للتسرّب متشقق أو متصلب عند الاختراقات",
      "فلاشينغ متدرج أو عداد مرفوع أو منفصل أو صدئ",
      "أحذية أنابيب مطاطية مهترئة",
      "دخول ماء أثناء مطر محمول بالرياح عند وصلات الجدار والسقف",
    ],
    evaluates: [
      "هندسة الفلاشينغ وحالة المادة",
      "التوافق مع الغطاء المحيط",
      "سلامة الفلاشينغ العداد والواجهة الجدارية",
      "حالة الحذاء والطوق عند الاختراق",
      "هل المادة المانعة للتسرّب يخفي تفصيلاً معدنياً فاشلاً",
    ],
    causes: [
      "حركة حرارية تفتح الوصلات",
      "شيخوخة الأشعة فوق البنفسجية والطقس للأحذية والمادة المانعة للتسرّب",
      "دمج غير صحيح للفلاشينغ المتدرج والعداد",
      "تآكل أو ضرر ميكانيكي للمعادن",
      "استبدال غطاء دون إعادة تفصيل الاختراقات",
    ],
    methodology: [
      "تحديد كل اختراق وواجهة جدار في منطقة العطل",
      "فتح فقط عند الحاجة لتأكيد مسار الماء",
      "استبدال الأحذية والأطواق أو المعادن الفاشلة بتفاصيل متوافقة",
      "إعادة دمج الفلاشينغ مع الغطاء بشكل صحيح",
      "تجنب الاعتماد على المادة المانعة للتسرّب السطحي كاستراتيجية عزل دائمة",
    ],
    materials: [
      "فلاشينغ متدرج وعداد ومريلة",
      "أحذية أنابيب وأطواق فتحات",
      "طبقة سفلية وأغشية ماء/جليد عند الواجهات الحرجة",
      "معادن مناسبة للتعرض الساحلي والمواد المجاورة",
      "مادة مانعة للتسرّب متوافق مع الشركة المصنعة كحماية ثانوية لا أساسية",
    ],
    bcClimate:
      "المطر المحمول بالرياح والمواسم الرطبة الطويلة تضغط على الفلاشينغ والأحذية المرنة. التفاصيل التي تنجح في مناخات أجف غالباً تفشل أبكراً في الساحل البريطاني دون دمج معدني صحيح.",
    whenAppropriate:
      "عندما يبقى غطاء السطح سقف قابلاً للخدمة ويكون التسرب مدفوعاً بفشل فلاشينغ أو اختراق معزول.",
    whenOtherService:
      "إذا كان الغطاء المحيط مهترئاً على نطاق واسع، أو احتاجت مدخنة أو نافذة سقفية أو نظام السقف منخفض الميل نطاقاً مختلفاً، قد تكون خدمات متخصصة أنسب.",
    solutionCategories: [
      {
        title: "استبدال حذاء الاختراق",
        body: "استبدال أحذية الأنابيب والفتحات المتقادمة قبل أن تتشقق وتدخل الماء حول فتحات الصرف أو الميكانيكية.",
      },
      {
        title: "إصلاح فلاشينغ الجدار والدرج",
        body: "استعادة واجهات الجدار والسقف حيث لا يعود الفلاشينغ المتدرج والعداد يصرف الماء.",
      },
      {
        title: "عمل واجهات المداخن المعقدة",
        body: "معالجة مكونات فلاشينغ متعددة المستويات تحتاج تسلسلاً دقيقاً مع الغطاء.",
      },
    ],
    repairVsReplace: {
      repair: "إصلاح الفلاشينغ مناسب عندما يكون سطح السقف سليماً والفشل مركزاً عند واجهة.",
      replace:
        "قد يلزم استبدال سطح السقف إذا كان دمج الفلاشينج من جديد يتطلب فتح غطاء شارف على نهاية عمره.",
    },
    scopeFactors: [
      "عدد ونوع الاختراقات",
      "واجهة تكسية الجدار",
      "توافق المعادن",
      "الوصول حول المداخن والنوافذ السقفية",
      "حالة الغطاء الحالي",
      "الحاجة لفتح المكونات المجاورة",
    ],
    process: [
      "صف موقع التسرب وأي تفاصيل مرئية حوله",
      "فحص على السطح للفلاشينغ والاختراقات في منطقة العطل",
      "نطاق إصلاح مكتوب يحدد كل تفصيل يُصحح",
      "إعادة تفصيل بمعادن وأحذية وأغشية متوافقة",
      "مراجعة نهائية للواجهات المصححة والغطاء المحيط",
    ],
    publicPriceHint:
      "يعكس التسعير عدد ونوع الاختراقات وتعقيد واجهة الجدار وكمية الغطاء المحيط التي يجب فتحها وإعادة دمجها. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو إصلاح الفلاشينغ واختراقات السقف؟",
        answer:
          "إصلاح الفلاشينغ والاختراقات يصحح واجهات تصريف الماء حيث يلتقي الغطاء بالجدران والمداخن والفتحات والأنابيب. هذه التفاصيل مصادر تسرب شائعة عند فشل المعادن أو الأحذية أو وصلات المادة المانعة للتسرّب.",
      },
      {
        question: "كيف أعرف إن كان تسربي مشكلة فلاشينغ؟",
        answer:
          "التسربات قرب المداخن أو الجدران الجانبية أو النوافذ السقفية أو أكوام الصرف أو الفتحات، خاصة أثناء مطر محمول بالرياح، غالباً مرتبطة بالفلاشينغ. البقع الداخلية المحاذية لاختراق فوقها مؤشر قوي، لكن التأكيد يحتاج فحصاً على السطح.",
      },
      {
        question: "هل يمكن إصلاح الفلاشينغ دون استبدال السقف؟",
        answer:
          "عادة نعم عندما يبقى الغطاء المحيط قابلاً للخدمة. يمكن استبدال فلاشينغات وأحذية وأطواق فردية وإعادة نسجها في السقف الحالي. إذا كان سطح السقف قرب نهاية عمره، يُفضل الجمع بين إعادة التفصيل وتخطيط الاستبدال.",
      },
      {
        question: "لماذا لم يوقف مادة مانعة للتسرّب الفلاشينغ التسرب؟",
        answer:
          "المادة المانعة للتسرّب يتقادم أسرع بكثير من المعدن ولا يعوض هندسة فلاشينغ خاطئة. المادة المانعة للتسرّب السطحي غالباً يخفي الفشل بينما يستمر الماء بالدخول. الإصلاح الدائم يعيد التفصيل المعدني؛ المادة المانعة للتسرّب دور ثانوي فقط.",
      },
      {
        question: "كم يدوم حذاء الأنبوب والفلاشينغ في الساحل البريطاني؟",
        answer:
          "العمر يختلف حسب المادة والتعرض وجودة التركيب، فلا رقم واحد صادق. أحذية المطاط عادة تتقادم أسرع من معادن الفلاشينغ لأن الأشعة فوق البنفسجية والدورات الحرارية تقسّي المطاط، والمطر المحمول بالرياح الساحلي يكشف التفاصيل الضعيفة أبكراً.",
      },
    ],
  },
  "moss-removal-roof-gutter": {
    title: "إزالة الطحلب وحزمة السقف والمزاريب",
    shortTitle: "طحلب ومزاريب",
    eyebrow: "حزمة صيانة",
    description:
      "معالجة الطحلب وإزالة الحطام وتنظيف المزاريب وفحص بصري للسقف لمنازل مترو فانكوفر حيث النمو العضوي والصرف المسدود يسرّعان تآكل السقف.",
    definition:
      "خدمة مجمعة لإزالة الطحلب وصيانة السقف والمزاريب تعالج النمو العضوي على أسطح السقف وتفرغ المزاريب والمصارف وتُوثق الحالة المرئية للتخطيط.",
    metaTitle: "إزالة الطحلب وتنظيف المزاريب فانكوفر | رولين روفينغ",
    metaDescription:
      "إزالة الطحلب وتفريغ الحطام وتنظيف المزاريب وفحص بصري للسقف في مترو فانكوفر. صيانة تركز على الصرف وصحة الغطاء.",
    forWhom:
      "أصحاب منازل لديهم طحلب أو أشنية أو حطام كثيف على الألواح أو في المزاريب، خاصة تحت الغطاء الشجري.",
    warningSigns: [
      "بساطات طحلب تحبس الرطوبة على الألواح",
      "مزاريب تفيض أثناء مطر عادي",
      "نمو عضوي على منحدرات مواجهة الشمال",
      "سدود حطام في الوديان",
      "مصارف لا تصرف بحرية",
    ],
    evaluates: [
      "مدى الطحلب والنمو العضوي",
      "حالة الغطاء تحت النمو",
      "تدفق المزاريب والمصارف",
      "حطام الوديان والحواف",
      "هل الضرر يتطلب إصلاحاً بالفعل",
    ],
    causes: [
      "ظروف ساحلية رطبة مظللة",
      "حطام الأشجار",
      "إهمال صيانة المزاريب",
      "ملمس سطح يحبس العضويات",
      "جفاف محدود لسطح السقف في الأشهر الباردة",
    ],
    methodology: [
      "حماية المناظر الطبيعية والوصول",
      "إزالة الطحلب والحطام بأساليب مناسبة للغطاء",
      "تنظيف المزاريب وشطف المصارف",
      "مراجعة بصرية للحالة",
      "تدوين أولويات إصلاح مكتشفة أثناء التنظيف",
    ],
    materials: [
      "أدوات تنظيف لطيفة مناسبة لنوع السقف",
      "منتجات معالجة معتمدة عند الاستخدام",
      "معدات كشط المزاريب والشطف",
    ],
    bcClimate:
      "ظل وأمطار اللوير مينلاند تخلق ظروفاً مثالية للطحلب. النمو يحبس الرطوبة على الغطاء وقد يسرّع فقدان الحبيبات إن تُرك.",
    whenAppropriate: "عندما يبقى الغطاء سليماً أساساً والنمو والحطام والصرف هي المشكلة الرئيسية.",
    whenOtherService:
      "ألواح مفقودة أو تسربات نشطة أو فلاشينغ فاشل تحتاج خدمات إصلاح لا تنظيفاً وحده.",
    solutionCategories: [
      {
        title: "إزالة الطحلب والعضويات",
        body: "تقليل النمو المحتفظ بالرطوبة على الأسطح المناسبة.",
      },
      { title: "تنظيف المزاريب والمصارف", body: "استعادة صرف حر على الحواف ونقاط التصريف." },
      { title: "مراجعة بصرية للسقف", body: "تدوين أضرار الغطاء بعد إزالة الحطام." },
    ],
    repairVsReplace: {
      repair: "التنظيف يناسب أسقفاً قابلة للخدمة حيث النمو والحطام هما الأساس.",
      replace: "إذا كشف التنظيف هشاشة واسعة، قد يكون تخطيط الاستبدال أصدق من تنظيف متكرر.",
    },
    scopeFactors: [
      "مساحة السقف",
      "الميل والوصول",
      "كثافة الغطاء الشجري",
      "طول المزاريب",
      "هشاشة الغطاء",
      "التخلص من الحطام",
    ],
    process: [
      "شارك نوع السقف والتعرض للظل وتفاصيل الوصول",
      "مراجعة موقعية لمدى النمو والصرف وحالة الغطاء",
      "نطاق عمل مكتوب للتنظيف والمعالجة وعمل المزاريب",
      "إزالة دقيقة ومعالجة عند الحاجة وشطف المصارف",
      "ملاحظات حالة مع صور لأي ما يحتاج إصلاحاً",
    ],
    publicPriceHint:
      "يعتمد التسعير على مساحة السقف والميل ومدى استقرار النمو وطول المزاريب والوصول. اطلب تقييماً لتسعير خاص بالمشروع.",
    faqs: [
      {
        question: "ما هي حزمة إزالة الطحلب والسقف/المزاريب؟",
        answer:
          "خدمة مجمعة لإزالة الطحلب وصيانة السقف والمزاريب تعالج النمو العضوي على أسطح السقف وتفرغ المزاريب والمصارف وتُوثق الحالة المرئية للتخطيط.",
      },
      {
        question: "هل الغسيل بالضغط العالي طريقة جيدة لإزالة طحلب السقف؟",
        answer:
          "الغسيل عالي الضغط غير مستحسن عموماً على الألواح الإسفلتية لأنه يزيل الحبيبات الواقية وقد يدفع الماء تحت الغطاء. يُزال الطحلب بأساليب ميكانيكية ألطف ومعالجات مناسبة للمادة.",
      },
      {
        question: "هل يضر الطحلب السقف فعلاً أم أنه مظهري فقط؟",
        answer:
          "الطحلب المستقر يحبس الرطوبة على الغطاء ويرفع حواف الألواح ويسرّع فقدان الحبيبات. على الأرز يبقي الخشب رطباً ويسرّع التآكل. إزالة النمو مبكراً أقل تكلفة بكثير من إصلاح التآكل.",
      },
      {
        question: "كم مرة يجب إزالة الطحلب من أسقف مترو فانكوفر؟",
        answer:
          "يعتمد على الظل والغطاء الشجري واتجاه السقف: المنحدرات الشمالية والمظللة تنمو أسرع. كثير من المنازل المحلية تستفيد من نظرة سنوية وتنظيف عند بدء تكوّن بساط، لا جدول ثابت.",
      },
      {
        question: "هل إزالة الطحلب توقف فيض المزاريب؟",
        answer:
          "غالباً يرتبط الاثنان: شظايا الطحلب وحطام السقف أسباب شائعة لانسداد المزاريب. هذه الحزمة تنظف الاثنين ليُحكم على الصرف. إذا استمر الفيض والمزاريب نظيفة، قد تحتاج المعادن أو الميل اهتماماً.",
      },
    ],
  },
  "roof-health-check-maintenance": {
    title: "فحص صحة السقف والصيانة",
    shortTitle: "فحص الصحة",
    eyebrow: "الحالة أولاً",
    description: "فحص وصور واستخدام مادة مانعة للتسرّب أو صيانة بسيط وتقرير حالة للتخطيط المستنير.",
    definition:
      "فحص صحة السقف هو تقييم بصري منظم للغطاء والفلاشينغ والصرف والمكونات المتاحة، عادة مع توثيق بالصور وبنود صيانة محدودة متفق عليها مسبقاً.",
    metaTitle: "فحص صحة السقف والصيانة فانكوفر | رولين روفينغ",
    metaDescription:
      "فحص صحة السقف وصور وأولويات صيانة لأصحاب منازل مترو فانكوفر. توثيق حالة واضح قبل قرارات الإصلاح أو الاستبدال.",
    forWhom:
      "أصحاب يستعدون للشراء أو البيع أو التجديد، أو يلاحظون علامات مبكرة، أو يريدون خط أساس موثق قبل موسم الأمطار.",
    warningSigns: [
      "عدم يقين حول عمر السقف أو حالته المتبقية",
      "بقع بسيطة بلا مصدر واضح",
      "حطام موسمي وتراكم طحلب",
      "مادة مانعة للتسرّب متقادم عند الاختراقات",
      "حاجة لخط أساس تخطيطي مكتوب",
    ],
    evaluates: [
      "حالة الغطاء حسب السطح",
      "الفلاشينغ والاختراقات",
      "واجهات الصرف والمزاريب",
      "مكونات التهوية المرئية",
      "ترتيب أولوية الصيانة مقابل الإصلاح",
    ],
    causes: [
      "شيخوخة طبيعية بلا توثيق",
      "صيانة مؤجلة",
      "تعرض جوي خاص بالموقع",
      "عمل سابق بجودة مجهولة",
      "احتياج قرار شراء أو تجديد",
    ],
    methodology: [
      "مراجعة المخاوف والتاريخ المتاح",
      "فحص مناطق السقف الخارجية بشكل منهجي",
      "تصوير حالات تمثيلية",
      "إنجاز صيانة بسيطة متفق عليها فقط",
      "تسليم ملخص حالة واضح وخيارات",
    ],
    materials: [
      "توثيق بالصور",
      "مادة مانعة للتسرّب أو مسامير بسيطة عند الإدراج",
      "تنظيف حطام محدود إن وُجد في النطاق",
      "ملاحظات حالة مكتوبة",
    ],
    bcClimate:
      "أمطار موسمية تجعل فحوص ما قبل الشتاء وبعد العواصف مفيدة لاكتشاف صرف مسدود ومواد مرفوعة وفشل فلاشينغ مبكر.",
    whenAppropriate: "عندما تحتاج وضوحاً قبل الالتزام بإصلاح أو استبدال أو تحقيق إضافي.",
    whenOtherService: "تسربات نشطة كبيرة أو فتحات عاصفة يجب أن تذهب لمسارات الطوارئ أو الإصلاح.",
    solutionCategories: [
      { title: "فحص موثق", body: "مراجعة منظمة لما هو مرئي ومتاح على نظام السقف." },
      {
        title: "صيانة بسيطة",
        body: "استخدام مادة مانعة للتسرّب أو حطام محدود فقط عند الإدراج والملاءمة.",
      },
      { title: "ملخص تخطيطي", body: "فصل المخاوف الفورية عن بنود المراقبة متوسطة الأجل." },
    ],
    repairVsReplace: {
      repair: "كثير من النتائج بنود صيانة أو إصلاح موضعي لا محفزات استبدال كامل.",
      replace: "يُناقش الاستبدال فقط عندما تدعم الحالة الملحوظة مساراً أوسع.",
    },
    scopeFactors: [
      "حجم وتعقيد السقف",
      "الوصول",
      "عمق التوثيق بالصور",
      "بنود الصيانة المشمولة",
      "عدد أنظمة السقف على العقار",
    ],
    process: [
      "احجز فحص صحة وصف أي مخاوف",
      "فحص خارجي منهجي مع توثيق بالصور",
      "إنجاز بنود صيانة بسيطة متفق عليها أثناء الزيارة",
      "ملخص حالة مكتوب بأولويات مرتبة",
      "مناقشة متابعة لأي إصلاحات موصى بها",
    ],
    publicPriceHint:
      "يعتمد نطاق فحص الصحة على حجم السقف وتعقيده والوصول وكمية التوثيق والصيانة البسيطة المشمولة. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو فحص صحة السقف والصيانة؟",
        answer:
          "فحص صحة السقف هو تقييم بصري منظم للغطاء والفلاشينغ والصرف والمكونات ذات الصلة المتاحة، عادة مع توثيق بالصور وبنود صيانة محدودة متفق عليها مسبقاً.",
      },
      {
        question: "كم مرة يجب فحص السقف؟",
        answer:
          "إيقاع محلي مفيد هو فحص قبل أمطار الخريف، وبعد أي عاصفة رياح كبيرة. المنازل تحت غطاء شجري كثيف، أو بأسقف في النصف الثاني من عمرها المتوقع، تستفيد من اهتمام أكثر تكراراً.",
      },
      {
        question: "ماذا أستلم بعد فحص صحة السقف؟",
        answer:
          "ملخص موثق بالصور لحالة الغطاء والفلاشينغ والصرف والتهوية، مفصول إلى بنود تحتاج إصلاحاً عاجلاً وبنود للمراقبة وبلي طبيعي. وثيقة تخطيط يمكنك التصرف بناءً عليها بوتيرتك.",
      },
      {
        question: "هل يستحق الفحص قبل شراء أو بيع منزل؟",
        answer:
          "نعم: السقف من أغلى مكونات المبنى تجديداً، وخط أساس موثق للحالة يقلل تخمين التفاوض. لاحظ أن هذا مراجعة حالة تسقيف لا فحص منزل رسمي قبل الشراء.",
      },
      {
        question: "هل تصلحون المشاكل المكتشفة أثناء الفحص؟",
        answer:
          "بنود صيانة بسيطة متفق عليها، مثل تجديد مادة مانعة للتسرّب بسيط عند الاختراقات، يمكن إنجازها أثناء الزيارة. النتائج الأكبر تُوثق وتُحدد نطاقها بشكل منفصل لتقرر بمعلومات واضحة لا ضغط فوري.",
      },
    ],
  },
  "shingle-spot-repair": {
    title: "إصلاح موضعي للألواح",
    shortTitle: "إصلاح موضعي",
    eyebrow: "إصلاح غطاء موضعي",
    description:
      "إصلاح ألواح إسفلتية مفقودة أو تالفة بالرياح أو متشققة في مناطق محدودة عندما يبقى السطح المحيط قابلاً للخدمة.",
    definition:
      "الإصلاح الموضعي للألواح يستبدل أو يثبت منطقة محدودة من الألواح التالفة دون إعادة تسقيف كاملة عندما يسمح التطابق والظروف المحيطة.",
    metaTitle: "إصلاح موضعي للألواح مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "إصلاح ألواح إسفلتية مفقودة أو متشققة أو تالفة بالرياح في مترو فانكوفر عندما يبقى سطح السقف قابلاً للخدمة.",
    forWhom: "أصحاب منازل لديهم ضرر ألواح معزول على سقف مائل سليم أساساً.",
    warningSigns: [
      "ألواح مفقودة بعد الرياح",
      "ألسنة متشققة",
      "مسامير أو طبقة سفلية مكشوفة في منطقة محدودة",
      "تجمع حبيبات من منطقة واحدة",
      "ترقيعات سابقة مرتفعة",
    ],
    evaluates: [
      "إمكانية مطابقة الألواح",
      "مرونة السطح المحيط",
      "حالة الطبقة السفلية محلياً",
      "تعرض السطح المتضرر",
      "هل الضرر معزول فعلاً",
    ],
    causes: [
      "رفع الرياح",
      "ضرر صدمي",
      "تشقق حراري",
      "ضرر من مشي الأقدام",
      "فشل شريط اللصق مع العمر",
    ],
    methodology: [
      "تأكيد إمكانية المطابقة",
      "إزالة الألواح التالفة بعناية",
      "استبدال الطبقة السفلية محلياً عند الحاجة",
      "تركيب ألواح مطابقة بتثبيت صحيح",
      "إغلاق فقط حيث يقتضيه التفصيل",
    ],
    materials: [
      "ألواح إسفلتية مطابقة",
      "طبقة سفلية محلية",
      "مسامير متوافقة",
      "مادة مانعة للتسرّب تفصيل فقط حيث محدد",
    ],
    bcClimate:
      "عواصف الرياح الساحلية والشتاء الرطب يجعلان الإصلاح الموضعي في الوقت المناسب مهماً قبل وصول الماء للركيزة.",
    whenAppropriate: "عندما يكون الضرر محدوداً وسطح السقف يقبل ألواحاً متوافقة.",
    whenOtherService: "هشاشة واسعة أو فشل متعدد الأسطح يشير لتخطيط استبدال إسفلتي كامل.",
    solutionCategories: [
      { title: "استبدال ألواح موضعي", body: "استعادة منطقة ضرر محكمة بمواد متوافقة." },
      {
        title: "لمسة حافة وقمة",
        body: "تصحيح فشل معزول عند الحواف عالية الإجهاد عندما يبقى معزولاً.",
      },
      { title: "فحص مسار التسرب", body: "التأكد أن ضرر الموضع لا يخفي فشل فلاشينغ." },
    ],
    repairVsReplace: {
      repair: "إصلاح موضعي عندما يبقى سطح السقف مرناً وقابلاً للمطابقة.",
      replace: "استبدال كامل عندما يكون التآكل منهجياً.",
    },
    scopeFactors: [
      "مساحة الضرر",
      "الميل والوصول",
      "مطابقة المادة",
      "احتياجات الطبقة السفلية",
      "نافذة الطقس",
    ],
    process: [
      "أرسل صور المنطقة المتضررة إن كانت مرئية بأمان",
      "تأكيد على السطح أن الضرر معزول فعلاً",
      "فحص مطابقة ملف ولون اللوح",
      "إزالة واستبدال الألواح المتأثرة بعناية",
      "مراجعة الإصلاح والسطح المحيط",
    ],
    publicPriceHint:
      "يعتمد إصلاح الموضع على مساحة الضرر والميل والوصول ومدى مطابقة الألواح البديلة. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو الإصلاح الموضعي للألواح؟",
        answer:
          "الإصلاح الموضعي للألواح يستبدل أو يثبت منطقة محدودة من الألواح التالفة دون إعادة تسقيف كاملة عندما يسمح التطابق والظروف المحيطة.",
      },
      {
        question: "انطارت بضع ألواح في عاصفة: هل هذا عاجل؟",
        answer:
          "عالجه بسرعة. الألواح المفقودة تكشف طبقة سفلية غير مصممة للتعرض طويل الأمد، والمطر التالي قد يصل للركيزة. إصلاح موضعي سريع يمنع عادة مشكلة أكبر بكثير.",
      },
      {
        question: "هل ستطابق الألواح البديلة سقفي الحالي؟",
        answer:
          "الألواح الجديدة نادراً تطابق المتقادمة تماماً لأن الموجودة تبهت وتفقد حبيبات. اختيار لون مناسب يقلل التباين، وتُناقش توقعات المطابقة بصدق قبل العمل.",
      },
      {
        question: "كم لوحة تالفة كثيرة للإصلاح الموضعي؟",
        answer:
          "لا عدد ثابت: المهم هل الضرر محصور وهل السطح المحيط لا يزال مرناً بما يكفي لقبول العمل دون تشقق. فشل متكرر عبر عدة أسطح عادة يعني أن تخطيط الاستبدال أوفر.",
      },
      {
        question: "هل يكفي إغلاق اللوح المتشقق بدل استبداله؟",
        answer:
          "المادة المانعة للتسرّب السطحي على لوح متشقق قصير العمر وقد يعقد الإصلاح لاحقاً. استبدال الألواح التالفة يعيد التداخل المقصود لتصريف الماء وهو عادة الأصلح على المدى.",
      },
    ],
  },
  "gutter-cleaning-downspout-flush": {
    title: "تنظيف المزاريب وشطف المصارف",
    shortTitle: "تنظيف مزاريب",
    eyebrow: "صيانة الصرف",
    description: "تنظيف المزاريب وشطف المصارف ليعمل صرف الحافة أثناء أمطار مترو فانكوفر.",
    definition:
      "تنظيف المزاريب يزيل الحطام من المزاريب والمصارف حتى يغادر ماء السقف خط الحافة دون فيض على الواجهات أو الأساسات.",
    metaTitle: "تنظيف المزاريب والمصارف فانكوفر | رولين روفينغ",
    metaDescription:
      "تنظيف مزاريب احترافي وشطف مصارف لمنازل مترو فانكوفر. استعادة صرف الحافة وتقليل خطر الفيض.",
    forWhom: "أصحاب يرون فيضاً أو نباتات في المزاريب أو بقعاً على الواجهة بعد مطر عادي.",
    warningSigns: [
      "ماء يتدفق فوق وجه المزرية",
      "نباتات في القنوات",
      "رش على الأساس",
      "واجهة ملطخة",
      "مصارف لا تصرف عند الاختبار",
    ],
    evaluates: [
      "حجم الحطام",
      "ميل القناة",
      "حالة المخرج والامتداد",
      "تثبيت الواجهة",
      "هل المعدن فشل بالفعل",
    ],
    causes: ["أوراق وطحلب", "تراكم إبر الصنوبر", "ميل مستقر", "شبكات مسدودة", "تنظيف موسمي مفقود"],
    methodology: [
      "تفريغ القنوات",
      "شطف المصارف",
      "فحص المخارج والامتدادات",
      "تدوين حالة المعدن",
      "التوصية بالحماية أو الاستبدال فقط عند التبرير",
    ],
    materials: ["أدوات تنظيف", "معدات شطف", "مسامير أساسية فقط إن وُجدت في النطاق"],
    bcClimate: "المطر المتكرر يجعل المزاريب المسدودة تبلل الواجهة والأساس بسرعة في اللوير مينلاند.",
    whenAppropriate: "عندما يبقى المعدن سليماً والحطام هو المشكلة الرئيسية.",
    whenOtherService: "مزاريب صدئة أو منفصلة أو صغيرة جداً قد تحتاج استبدالاً.",
    solutionCategories: [
      { title: "تنظيف القنوات", body: "إزالة الحطام العضوي والحبيبي من المزاريب." },
      { title: "شطف المصارف", body: "استعادة مسارات تصريف متصلة." },
      { title: "ملاحظات الحالة", body: "الإبلاغ عن معدن أو واجهة فاشلة أثناء التنظيف." },
    ],
    repairVsReplace: {
      repair: "تنظيف عندما تبقى القنوات سليمة إنشائياً.",
      replace: "استبدال عندما لا يؤدي المعدن أو الميل بعد التنظيف.",
    },
    scopeFactors: [
      "الأمتار الخطية",
      "الطوابق والوصول",
      "نوع الحطام",
      "عدد المصارف",
      "متطلبات السلامة",
    ],
    process: [
      "شارك ارتفاع المنزل وطول المزاريب وملاحظات الوصول",
      "تفريغ الأوراق والإبر والحطام الحبيبي",
      "شطف كل مصرف وتأكيد تصريف حر",
      "تدوين حالة المعدن والميل والواجهة",
      "الإبلاغ عما لا يحله التنظيف وحده",
    ],
    publicPriceHint:
      "يعتمد التسعير عموماً على الأمتار الخطية وعدد الطوابق وحمل الحطام ومتطلبات السلامة والوصول. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو تنظيف المزاريب وشطف المصارف؟",
        answer:
          "تنظيف المزاريب يزيل الحطام من المزاريب والمصارف حتى يغادر ماء السقف خط الحافة دون فيض على الواجهات أو الأساسات.",
      },
      {
        question: "كم مرة يُنظف المزرية في مترو فانكوفر؟",
        answer:
          "معظم المنازل تحتاج على الأقل تنظيفاً خريفياً بعد سقوط الأوراق؛ العقارات تحت الصنوبر غالباً تحتاج زيارة ثانية لأن الإبر تتراكم طوال العام. الفيض في مطر عادي علامة أنك انتظرت طويلاً.",
      },
      {
        question: "ماذا يحدث إن تخطيت تنظيف المزاريب؟",
        answer:
          "المزاريب المسدودة تفيض على الواجهة والتكسية وتسقط الماء عند الأساس وقد تدفع الماء تحت حافة السقف. في هذا المناخ يحول ذلك غالباً مهمة صيانة بسيطة إلى تعفن واجهة أو إصلاح صرف.",
      },
      {
        question: "هل تشطفون المصارف أم تنظفون القنوات فقط؟",
        answer:
          "كلاهما. قناة نظيفة تصرف في مصرف مسدود ما زالت تفيض. يُشطف كل مصرف ويُؤكد تصريفه لأن انسداد المرفق السفلي والتحت أرضي من أكثر الأعطال الخفية شيوعاً.",
      },
      {
        question: "مزاريبي تفيض رغم نظافتها: ماذا بعد؟",
        answer:
          "ذلك يشير لمشكلة ميل أو حجم أو مخرج لا للحطام. زيارات التنظيف تتضمن ملاحظات حالة، وإن لم يؤدِ المعدن أو الهندسة يُوصى بالإصلاح أو الاستبدال مع توثيق الأسباب.",
      },
    ],
  },
  "storm-tarping-emergency-dry-in": {
    title: "تغطية عاصفة / عزل مائي طارئ",
    shortTitle: "تغطية طارئة بعد العاصفة",
    eyebrow: "عزل مؤقت",
    description: "عزل مؤقت ضد الماء بعد ضرر الرياح أو الأشجار أو العاصفة حتى يمكن تنفيذ عمل دائم.",
    definition:
      "تغطية العاصفة والعزل المائي الطارئ يحميان مؤقتاً الفتحات أو المناطق التالفة من الطقس لتقليل ضرر الداخل أثناء تخطيط الإصلاح الدائم.",
    metaTitle: "تغطية وعزل مائي طارئ بعد العواصف في فانكوفر | رولين روفينغ",
    metaDescription:
      "تغطية سقف طارئة وعزل مائي مؤقت بعد عاصفة أو شجرة في مترو فانكوفر. تثبيت الفتحات حتى يُخطط الإصلاح الدائم.",
    forWhom:
      "أصحاب لديهم ركيزة مكشوفة أو اصطدام شجرة أو مساحات كبيرة من غطاء السقف مفقودة بعد عاصفة.",
    warningSigns: [
      "ركيزة مكشوفة بعد شجرة",
      "مساحات كبيرة من غطاء السقف مفقودة",
      "دخول مطر داخلي عبر فتحة جديدة",
      "حطام غير مستقر على السقف",
      "حاجة حماية مؤقتة قبل مواد دائمة",
    ],
    evaluates: [
      "السلامة والوصول",
      "حجم وموقع الفتحة",
      "خطر داخلي",
      "نافذة الطقس",
      "مسار الإصلاح الدائم",
    ],
    causes: ["عواصف رياح", "سقوط شجرة", "فقدان غطاء مفاجئ", "ضرر صدمي", "فشل جزئي لمواد متقادمة"],
    methodology: [
      "تقييم السلامة أولاً",
      "إزالة ما يلزم فقط للتغطية الآمنة",
      "تركيب عزل مؤقت",
      "توثيق الحالة",
      "تخطيط مسار الإصلاح الدائم",
    ],
    materials: ["أغطية وأغشية مؤقتة", "تثبيت آمن مناسب للعمل المؤقت", "حماية للفتحات والحواف"],
    bcClimate:
      "عواصف المحيط الهادئ قد تفتح الأسقف بسرعة؛ العزل المائي المؤقت يحد الضرر الثانوي للداخل أثناء تحديد النطاق الدائم.",
    whenAppropriate: "عندما يجب عزل فتحة أو منطقة غير مستقرة من الطقس قبل العمل الدائم.",
    whenOtherService:
      "العزل المائي المؤقت ليس بديلاً دائماً عن إصلاح أو استبدال نظام السقف التالف.",
    solutionCategories: [
      { title: "عزل مؤقت", body: "تقليل دخول الطقس عبر فتحات العاصفة." },
      { title: "تثبيت", body: "تأمين مواد فضفاضة تهدد مزيداً من الضرر." },
      { title: "توثيق", body: "تسجيل الحالة لدعم تخطيط الإصلاح الدائم." },
    ],
    repairVsReplace: {
      repair: "عزل مائي مؤقت أولاً عندما لا يمكن إكمال العمل الدائم فوراً.",
      replace: "إصلاح أو استبدال دائم يتبع عندما يمكن استعادة نظام السقف بشكل صحيح.",
    },
    scopeFactors: ["حجم الفتحة", "الوصول والارتفاع", "الطقس", "التعرض الداخلي", "مخاطر الحطام"],
    process: [
      "تواصل مع صور وتفاصيل سلامة إن توفرت",
      "تقييم المخاطر والوصول في الموقع",
      "تثبيت الحطام فقط عند الحاجة للتغطية الآمنة",
      "تأمين عزل مؤقت فوق الفتحة",
      "توثيق الحالة وتخطيط الإصلاح الدائم",
    ],
    publicPriceHint:
      "يعتمد نطاق العزل المائي المؤقت على حجم الفتحة وارتفاع السقف والطقس أثناء العمل ومخاطر الحطام. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هي تغطية العاصفة / العزل المائي الطارئ؟",
        answer:
          "تغطية العاصفة والعزل المائي الطارئ يحميان مؤقتاً الفتحات أو المناطق التالفة من الطقس لتقليل ضرر الداخل أثناء تخطيط الإصلاح الدائم.",
      },
      {
        question: "شجرة اصطدمت بسقفي: ماذا أفعل أولاً؟",
        answer:
          "أبقِ الناس بعيداً عن الغرف الواقعة تحت الضرر، ولا تصعد إلى السقف، وصوّر ما تستطيع بأمان من الأرض والداخل. ثم اطلب تغطية السقف وتأمينه على يد مختص؛ فالأغصان غير المستقرة والأضرار الإنشائية الخفية تجعل السقف المتضرر من العاصفة خطراً.",
      },
      {
        question: "كم يمكن أن تبقى غطاء السقف؟",
        answer:
          "الأغطية حماية طقس مؤقتة لا تسقيفاً. عمرها العملي أسابيع إلى أشهر قليلة حسب التعرض والتثبيت وعواصف الشتاء. يُجدول الإصلاح الدائم بأسرع ما تسمح الظروف والمواد.",
      },
      {
        question: "هل يغطي التأمين تغطية الطوارئ؟",
        answer:
          "تعدّ كثير من وثائق تأمين الممتلكات الحماية المؤقتة المعقولة بعد الضرر المفاجئ خطوة للحد من تفاقم الخسائر، لكن التغطية الفعلية تعتمد على شروط وثيقتك. توثق رولين الحالة والعمل المنفذ للمساعدة في المطالبة، لكنها لا تقدم استشارة تأمينية.",
      },
      {
        question: "هل العزل المائي المؤقت بديل عن إصلاح السقف؟",
        answer:
          "لا. العزل المائي المؤقت يحد ضرر الداخل أثناء تخطيط إصلاح أو استبدال صحيح. ترك الحماية المؤقتة طويلاً يؤدي عادة لتسرب تدريجي مع تدهور التثبيت والمواد.",
      },
    ],
  },
  "skylight-leak-flashing-repair": {
    title: "إصلاح تسرب وفلاشينغ النوافذ السقفية",
    shortTitle: "إصلاح نافذة سقفية",
    eyebrow: "واجهات النوافذ السقفية",
    description: "إصلاح أو إعادة فلاشينغ للنوافذ السقفية المتسربة والتسقيف المحيط بها.",
    definition:
      "إصلاح تسرب وفلاشينغ النوافذ السقفية يعالج دخول الماء عند حواف الوحدة والفلاشينغ والتسقيف المجاور لا افتراض أن الزجاج وحده هو السبب.",
    metaTitle: "إصلاح تسرب النوافذ السقفية فانكوفر | رولين روفينغ",
    metaDescription:
      "تشخيص تسرب وإصلاح فلاشينغ حافة النافذة السقفية في مترو فانكوفر. تصحيح واجهات السقف حول النوافذ لا أختام الزجاج فقط.",
    forWhom: "أصحاب لديهم بقع تحت نوافذ سقفية أو فلاشينغ حافة فاشل أو شك هل التكثف أم تسرب حقيقي.",
    warningSigns: [
      "بقع تحت آبار النوافذ",
      "طلاء متقشر عند عمود الضوء",
      "ضوء نهار عند زوايا الحافة",
      "فلاشينغ متدرج فاشل بجانب الوحدات",
      "ماء راكد عند الحواف على أسطح منخفضة الميل",
    ],
    evaluates: [
      "نوع وعمر النافذة",
      "حالة الحافة والفلاشينغ",
      "دمج الغطاء المجاور",
      "أنماط التكثف الداخلي",
      "هل يلزم استبدال الوحدة",
    ],
    causes: [
      "فشل فلاشينغ الحافة",
      "دمج خاطئ مع ألواح أو غشاء",
      "أختام متقادمة",
      "تكثف يُشخّص خطأ كتسرب",
      "حركة عند الفتحات المؤطرة",
    ],
    methodology: [
      "التمييز بين التسرب والتكثف حيث أمكن",
      "فحص فلاشينغ الحافة والتسقيف المجاور",
      "إعادة فلاشينغ أو إصلاح تفاصيل متوافقة",
      "معالجة الغطاء المحيط عند الحاجة",
      "التوصية باستبدال الوحدة فقط عند التبرير",
    ],
    materials: [
      "فلاشينغ حافة",
      "طبقة سفلية أو تفاصيل غشاء متوافقة",
      "ترقيع ألواح أو غشاء محيط",
      "مادة مانعة للتسرّب كإجراء ثانوي فقط",
    ],
    bcClimate:
      "المطر المحمول بالرياح يجد حواف النوافذ الضعيفة بسرعة في الساحل البريطاني؛ حالة الزجاج وحدها نادراً تفسر كل تسرب.",
    whenAppropriate: "عندما تكون الوحدة قابلة للخدمة والفشل في الفلاشينغ أو التسقيف المجاور.",
    whenOtherService:
      "إطارات فاشلة أو وحدات محكمة تتسرب باستمرار أو فشل سقف أوسع قد تحتاج نطاقاً مختلفاً.",
    solutionCategories: [
      { title: "إعادة فلاشينغ الحافة", body: "استعادة واجهات المعدن والغشاء عند حافة النافذة." },
      { title: "إصلاح الغطاء المجاور", body: "تصحيح ألواح أو غشاء لم يعد متكاملاً مع الوحدة." },
      {
        title: "مراجعة تسرب مقابل تكثف",
        body: "مساعدة في فصل تسرب نظام السقف عن أعراض رطوبة داخلية.",
      },
    ],
    repairVsReplace: {
      repair: "إصلاح عندما تقبل الحافة والوحدة إعادة تفصيل دائمة.",
      replace: "استبدال وحدة أو سقف عندما لا يمكن جعل نظام السقف موثوقاً.",
    },
    scopeFactors: [
      "عدد وحجم النوافذ",
      "نوع نظام السقف",
      "ارتفاع الحافة وحالتها",
      "الوصول",
      "تشطيبات داخلية معرضة للخطر",
    ],
    process: [
      "صف نمط البقعة وعمر النافذة إن عُرف",
      "فحص الحافة والفلاشينغ والتسقيف المجاور",
      "التمييز بين أدلة التسرب والتكثف",
      "إعادة فلاشينغ أو إصلاح التفاصيل الفاشلة",
      "التحقق من الواجهة وتوثيق العمل",
    ],
    publicPriceHint:
      "يُسعّر عمل النوافذ السقفية حسب سياق كل وحدة: حالة الحافة ونوع السقف ومتطلبات الفلاشينغ والوصول. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو إصلاح تسرب وفلاشينغ النوافذ السقفية؟",
        answer:
          "إصلاح تسرب وفلاشينغ النوافذ السقفية يعالج دخول الماء عند حواف الوحدة والفلاشينغ والتسقيف المجاور لا افتراض أن الزجاج وحده هو السبب.",
      },
      {
        question: "هل تتسرب نافذتي أم أنها تكثف؟",
        answer:
          "التكثف يظهر كقطرات دقيقة أو ضباب على الزجاج والإطار، غالباً في الطقس البارد والغرف الرطبة. التسرب يتبع من زوايا الحافة أو الحافة العلوية ويلطخ الجبس في عمود الضوء. التشخيص على السطح يؤكد أيهما.",
      },
      {
        question: "هل تسرب النافذة يعني أنني أحتاج نافذة جديدة؟",
        answer:
          "ليس بالضرورة. كثير من التسربات من الفلاشينغ أو التسقيف المحيط لا الوحدة. إذا فشل ختم الزجاج، ضباب دائم بين الزجاجين، أو تدهورت الإطار، يُناقش الاستبدال؛ وإلا إعادة الفلاشينغ عادة تحل المشكلة.",
      },
      {
        question: "هل تُستبدل النوافذ أثناء إعادة التسقيف؟",
        answer:
          "غالباً اللحظة العملية: يجب إعادة الفلاشينغ على أي حال والوصول موجود، ووحدة متقادمة نادراً تتجاوز السقف الجديد حولها. وحدة سليمة يمكن الإبقاء عليها وإعادة فلاشينغها بشكل صحيح.",
      },
      {
        question: "لماذا تتسرب فقط في مطر محمول بالرياح؟",
        answer:
          "المطر المدفوع بالرياح يحمّل تفاصيل الفلاشينغ بشكل مختلف عن المطر الساقط: قد يتسلق الطبقات ويدخل تفاصيل تصرف المطر العمودي جيداً. هذا النمط يشير بقوة لمشكلة هندسة فلاشينغ لا ختم زجاج.",
      },
    ],
  },
  "chimney-flashing-repair": {
    title: "إصلاح فلاشينغ المدخنة",
    shortTitle: "فلاشينغ مدخنة",
    eyebrow: "واجهات المدخنة",
    description: "فلاشينغ متدرج وعداد وتصحيح تسربات مرتبطة بالمدخنة على أسقف مترو فانكوفر.",
    definition:
      "إصلاح فلاشينغ المدخنة يعيد واجهات المعدن التي تصرف الماء حيث تلتقي مدخنة بناء أو مؤطرة بسطوح السقف.",
    metaTitle: "إصلاح فلاشينغ المدخنة مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "إصلاح فلاشينغ متدرج وعداد للمداخن في مترو فانكوفر. تصحيح مسارات تسرب المدخنة بتفاصيل معدنية صحيحة.",
    forWhom: "أصحاب لديهم تسربات عند زوايا المدخنة أو عداد صدئ أو تفاصيل حاجز تصريف متدهورة.",
    warningSigns: [
      "بقع زوايا المدخنة",
      "خطوط صدأ من العداد",
      "مشاكل ملاط مع تسرب أسفلها",
      "حاجز تصريف مفقود على مداخن عريضة",
      "قطران سابق عند قاعدة المدخنة",
    ],
    evaluates: [
      "حجم وهندسة المدخنة",
      "حالة الفلاشينغ المتدرج والعداد",
      "حاجة حاجز تصريف علوي",
      "واجهات البناء",
      "حالة الغطاء المحيط",
    ],
    causes: [
      "فلاشينغ متدرج غير منسوج بشكل صحيح",
      "عداد مفصول عن البناء",
      "حركة حرارية",
      "حاجز تصريف علوي مفقود",
      "العمر والتآكل",
    ],
    methodology: [
      "فحص كل وجه وتقاطع سقف",
      "فتح فقط لإعادة مسارات المعدن",
      "تركيب متدرج وعداد بشكل صحيح",
      "إضافة أو استعادة حاجز تصريف عند الحاجة",
      "إعادة دمج الغطاء المحيط",
    ],
    materials: [
      "فلاشينغ متدرج وعداد",
      "مواد إطار وحاجز تصريف عند الحاجة",
      "طبقة سفلية متوافقة",
      "ملاط أو مادة مانعة للتسرّب كتفاصيل ثانوية فقط",
    ],
    bcClimate:
      "زوايا المدخنة مناطق رطبة عالية الإجهاد في مطر اللوير مينلاند المحمول بالرياح؛ القطران السطحي ليس بديلاً دائماً لمسارات المعدن.",
    whenAppropriate: "عندما تدعم المدخنة والسطح المحيط إعادة فلاشينغ صحيحة.",
    whenOtherService:
      "إذا كان سطح السقف قرب نهاية عمره، يُفضل الجمع بين إعادة الفلاشينغ وتخطيط الاستبدال.",
    solutionCategories: [
      { title: "استعادة الفلاشينغ المتدرج", body: "إعادة مسارات معدنية منسوجة مع الغطاء." },
      {
        title: "إصلاح الفلاشينغ العلوي",
        body: "إحكام الحافة المعدنية العلوية عند البناء أو كسوة الواجهة.",
      },
      {
        title: "تفاصيل حاجز التصريف والانحراف",
        body: "تحسين تصريف الماء العلوي على مداخن عريضة عند الحاجة.",
      },
    ],
    repairVsReplace: {
      repair: "إعادة فلاشينغ مدخنة عندما يبقى سطح السقف قابلاً للخدمة.",
      replace: "الجمع مع إعادة تسقيف عندما لا يقبل سطح السقف دمجاً دائماً.",
    },
    scopeFactors: ["عرض المدخنة", "ميل السقف", "حالة البناء", "الوصول", "عمر الغطاء المحيط"],
    process: [
      "شارك صور المدخنة وأي بقع داخلية",
      "فحص كل وجوه المدخنة وتقاطعات السقف",
      "نطاق عمل مكتوب للمسارات المعدنية المطلوب إعادة بنائها",
      "تركيب متدرج وعداد وحاجز تصريف بشكل صحيح",
      "إعادة دمج الغطاء ومراجعة النتيجة",
    ],
    publicPriceHint:
      "يُسعّر عمل المدخنة حول العرض والميل وحالة البناء وكمية الغطاء الذي يجب رفعه وإعادة دمجه. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو إصلاح فلاشينغ المدخنة؟",
        answer:
          "إصلاح فلاشينغ المدخنة يعيد واجهات المعدن التي تصرف الماء حيث تلتقي مدخنة بناء أو مؤطرة بسطوح السقف.",
      },
      {
        question: "لماذا تتسرب مدخنتي رغم أن السقف جديداً؟",
        answer:
          "فلاشينغ المدخنة نقطة ضعف شائعة بعد إعادة التسقيف: إذا أُعيد استخدام العداد أو لم يُنسج المتدرج بشكل صحيح، يصرف الغطاء الجديد الماء مباشرة إلى فشل قديم. واجهة المدخنة تُفصّل كنظام سقف مستقل.",
      },
      {
        question: "ما هو العداد ولماذا يهم؟",
        answer:
          "المتدرج يحمل الماء فوق الألواح؛ العداد هو المسار العلوي المدخل في البناء يغطي الساق الرأسية للمتدرج. بدون إنهاء عداد سليم، الماء النازل على وجه المدخنة يذهب خلف المعدن المتدرج.",
      },
      {
        question: "هل أحتاج حاجز تصريف خلف مدخنتي؟",
        answer:
          "المداخن العريضة على الجانب المنحدر تجمع الماء والحطام على الوجه العلوي. حاجز التصريف: حافة صغيرة منحرفة، يصرف الماء حول المدخنة. الحاجة تعتمد على العرض والميل والحالة الملحوظة.",
      },
      {
        question: "هل قطران التسقيف إصلاح فلاشينغ مقبول؟",
        answer:
          "كوقف طوارئ قد يبطئ التسرب. كإصلاح يفشل: ينكمش ويتشقق ويحبس الماء خلفه مخفياً فشل المعدن. التصحيح الدائم يعيد مسارات المعدن.",
      },
    ],
  },
  "gutter-replacement": {
    title: "استبدال المزاريب",
    shortTitle: "استبدال مزاريب",
    eyebrow: "أنظمة صرف الحافة",
    description: "استبدال مزاريب وأنابيب ألومنيوم سلسة لصرف حافة موثوق.",
    definition:
      "استبدال المزاريب يركب قنوات ومصارف جديدة بحجم وميل مناسبين لنقل ماء السقف بعيداً عن غلاف المبنى.",
    metaTitle: "استبدال المزاريب مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "استبدال مزاريب وأنابيب ألومنيوم سلسة لمنازل مترو فانكوفر. ميل ومخارج وأداء صرف حافة صحيح.",
    forWhom: "أصحاب لديهم فيض متكرر بعد التنظيف أو مزاريب صدئة أو منفصلة أو ضرر واجهة من صرف فاشل.",
    warningSigns: [
      "انشقاقات عند الوصلات",
      "مزاريب تنفصل عن الواجهة",
      "فيض متكرر بعد التنظيف",
      "ثقوب صدأ",
      "ميل خاطئ لا يُصحح في المكان",
    ],
    evaluates: [
      "الأمتار الخطية والطوابق",
      "تصميم المخارج",
      "حالة الواجهة",
      "اللون والملف",
      "توجيه المصارف",
    ],
    causes: ["نهاية عمر المعدن", "قنوات صغيرة", "حركة الواجهة", "حمل حطام", "تركيب أصلي ضعيف"],
    methodology: [
      "تأكيد جاهزية الواجهة",
      "تخطيط الميل والمخارج",
      "تركيب قنوات سلسة ومصارف",
      "اختبار مسارات التدفق",
      "معالجة الرش ونقاط التصريف",
    ],
    materials: ["مزاريب ألومنيوم سلسة", "مصارف ومرفقات", "علاقات ومسامير", "سدادات ومخارج"],
    bcClimate: "أحجام أمطار عالية تجعل حجم وميل المزاريب الصحيحين ضروريين لمنازل اللوير مينلاند.",
    whenAppropriate: "عندما لا تؤدي المزاريب الحالية حتى بعد التنظيف والإصلاح البسيط.",
    whenOtherService: "إذا كانت القنوات سليمة، قد يكفي التنظيف أو الحماية.",
    solutionCategories: [
      { title: "أنظمة حافة سلسة", body: "قنوات جديدة بميل نحو مخارج محكومة." },
      { title: "تصميم المصارف", body: "توجيه التصريف بعيداً عن الأساسات والممرات." },
      {
        title: "تنسيق ألواح الواجهة",
        body: "إصلاح ألواح الواجهة الرخوة أو المتعفنة قبل تركيب المعدن الجديد عند الحاجة.",
      },
    ],
    repairVsReplace: {
      repair: "استبدال عند فشل المعدن أو الهندسة.",
      replace: "تنظيف أو إصلاح فقط عندما يبقى النظام سليماً.",
    },
    scopeFactors: [
      "الأمتار الخطية",
      "الارتفاع/الوصول",
      "عدد المخارج",
      "اختيار اللون",
      "إصلاحات الواجهة",
    ],
    process: [
      "قياس المسارات والمخارج وتوجيه المصارف",
      "تأكيد أن الواجهة تتحمل علاقات جديدة",
      "نطاق عمل مكتوب باختيار الملف واللون",
      "تشكيل وتركيب قنوات سلسة في الموقع",
      "اختبار التدفق ومراجعة التصريف",
    ],
    publicPriceHint:
      "يعتمد سعر الاستبدال على الأمتار الخطية والطوابق والوصول وعدد المخارج وأي إصلاح واجهة مطلوب أولاً. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو استبدال المزاريب؟",
        answer:
          "استبدال المزاريب يركب قنوات ومصارف جديدة بحجم وميل مناسبين لنقل ماء السقف بعيداً عن غلاف المبنى.",
      },
      {
        question: "ماذا يعني مزرية سلسة فعلاً؟",
        answer:
          "المزاريب السلسة تُشكّل في الموقع بأطوال متصلة لكل مسار، فتكون الوصلات عند الزوايا والمخارج فقط. وصلات أقل تعني نقاط تسرب مستقبلية أقل مقارنة بالقطع المتصلة كل بضعة أقدام.",
      },
      {
        question: "هل يُستبدل المزرية مع السقف؟",
        answer:
          "إذا كان الاثنان قرب نهاية العمر، تسلسلهما معاً يتجنب إتلاف مزاريب جديدة أثناء الإزالة ويسمح بتنسيق حافة التقطير وموضع القناة. نظام سليم لا يحتاج استبدالاً لمجرد أن السقف يُستبدل.",
      },
      {
        question: "ما حجم المزاريب الذي يحتاجه منزل مترو فانكوفر؟",
        answer:
          "الحجم يعتمد على مساحة السقف المصرفة لكل مسار والميل وموضع المخرج لا إجابة واحدة. شدة الأمطار المحلية تكشف القنوات الصغيرة أو ناقصة التصريف بسرعة، فيُقيّم التخطيط لكل منزل.",
      },
      {
        question: "كيف تبعدون ماء المصارف عن الأساس؟",
        answer:
          "يُخطط التصريف نحو صرف مناسب: امتدادات وإدارة رش أو صرف أرضي موجود، حتى لا يُصب ماء السقف على القبو. تدرج الموقع وترتيبات الصرف تشكل الإجابة الصحيحة.",
      },
    ],
  },
  "gutter-guard-installation": {
    title: "تركيب حماية المزاريب",
    shortTitle: "حماية مزاريب",
    eyebrow: "حماية من الحطام",
    description:
      "أنظمة حماية من الأوراق والطحلب والحطام تقلل تكرار الانسداد على عقارات مظللة بالأشجار.",
    definition:
      "تركيب حماية المزاريب يضيف نظاماً فوق القنوات لتقليل تراكم الأوراق والحطام مع السماح بدخول الماء.",
    metaTitle: "تركيب حماية المزاريب مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "تركيب حماية مزاريب لمنازل مترو فانكوفر ذات حطام شجري كثيف. تقليل تكرار الانسداد على أنظمة سليمة.",
    forWhom:
      "أصحاب يحتاجون تنظيفاً متكرراً أو يواجهون وصولاً صعباً لطوابق متعددة أو يعيشون تحت غطاء شجري كثيف.",
    warningSigns: [
      "تنظيف مطلوب مرات عديدة سنوياً",
      "وصول صعب لطوابق متعددة",
      "غطاء إبر صنوبر كثيف",
      "انسداد مصارف بعد تنظيف قريب",
      "قيود وصول للصيانة",
    ],
    evaluates: [
      "حالة المزاريب إنشائياً أولاً",
      "ملف الحطام",
      "ملاءمة المنتج",
      "هندسة حافة السقف",
      "توقعات الصيانة",
    ],
    causes: [
      "مناظر كثيفة",
      "هندسة سقف تسقط الحطام في القنوات",
      "حطام دقيق يهزم الشبكات البسيطة",
      "فترات صيانة غير منتظمة",
    ],
    methodology: [
      "تأكيد سلامة المزاريب",
      "اختيار نظام حماية مناسب",
      "التركيب مع الانتباه لدخول الماء والحواف",
      "اختبار التدفق",
      "شرح احتياجات الصيانة المتبقية بصدق",
    ],
    materials: ["مكونات نظام الحماية", "مسامير متوافقة مع المزاريب", "إكسسوارات حافة عند الحاجة"],
    bcClimate:
      "أشجار الساحل تسقط إبراً وشظايا طحلب تهزم جداول تنظيف نادرة؛ الحماية تقلل ولا تلغي كل الصيانة.",
    whenAppropriate: "عندما تكون المزاريب سليمة إنشائياً وحمل الحطام هو المشكلة الرئيسية.",
    whenOtherService: "مزاريب فاشلة يجب استبدالها قبل تركيب الحماية.",
    solutionCategories: [
      { title: "أنظمة حماية من الحطام", body: "تقليل دخول الأوراق والإبر إلى القنوات." },
      { title: "فحص التوافق", body: "التركيب فقط على مزاريب ما زالت تصرف بشكل صحيح." },
      { title: "توقعات الصيانة", body: "توضيح ما تقلله الحماية وما يحتاج اهتماماً دورياً." },
    ],
    repairVsReplace: {
      repair: "تركيب حماية على مزاريب سليمة بحمل حطام عالٍ.",
      replace: "استبدال المزاريب أولاً عند فشل المعدن.",
    },
    scopeFactors: [
      "الأمتار الخطية",
      "نوع المنتج",
      "ملف حطام السقف",
      "الوصول",
      "حالة المزاريب الحالية",
    ],
    process: [
      "تقييم حالة المزاريب وملف حطام العقار",
      "التوصية بنوع حماية يناسب الأشجار المحيطة",
      "نطاق عمل مكتوب يتضمن أي تنظيف مسبق",
      "تركيب الحماية مع الانتباه لدخول الماء والحواف",
      "شرح إيقاع الصيانة المتبقي بصدق",
    ],
    publicPriceHint:
      "يعتمد سعر الحماية على نوع المنتج والأمتار الخطية وهندسة حافة السقف وحالة المزاريب الحالية. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو تركيب حماية المزاريب؟",
        answer:
          "تركيب حماية المزاريب يضيف نظاماً فوق القنوات لتقليل تراكم الأوراق والحطام مع السماح بدخول الماء.",
      },
      {
        question: "هل تعني الحماية أنني لن أنظف المزاريب أبداً؟",
        answer:
          "لا. الحماية تقلل كثيراً تكرار التنظيف لكن لا تلغيه: يتراكم الحطام فوق الحماية والوديان ما زالت تجمع المواد والمخارج تحتاج فحصاً دورياً. الإطار الصادق: تنظيفات أقل، ليس صفر.",
      },
      {
        question: "هل تعمل الحماية مع إبر الصنوبر؟",
        answer:
          "الإبر الدقيقة تهزم الشبكات المثقبة البسيطة. منتجات الشبكة الدقيقة أداء أفضل تحت الصنوبر، رغم أن طبقة حطام دقيق قد تتراكم فوقها وتحتاج فركاً أحياناً. اختيار المنتج يبدأ مما يسقط فعلاً على سقفك.",
      },
      {
        question: "هل يمكن تركيب الحماية على مزاريبي الحالية؟",
        answer:
          "نعم إن كانت سليمة إنشائياً وبميل صحيح ومتثبتة جيداً. تركيب حماية فوق مزاريب فاشلة يحبس المشكلة الأساسية، فيُتحقق من الحالة أولاً: أحياناً بتنظيف وإعادة ضبط بسيطة.",
      },
      {
        question: "هل تسبب الحماية مشاكل في مطر غزير؟",
        answer:
          "نظام مختار ومركب بشكل صحيح يمرر أحجام أمطار محلية. حماية غير مناسبة قد تسبب تجاوزاً في أمطار شديدة خاصة تحت سطوح شديدة الانحدار، لذلك ميل السقف وسرعة الماء عند الحافة توجّه الاختيار.",
      },
    ],
  },
  "roof-ventilation-upgrade": {
    title: "ترقية تهوية السقف",
    shortTitle: "ترقية تهوية",
    eyebrow: "تدفق هواء العلية",
    description: "فتحات صندوقية وقمة ومدخل سوفيت وتصحيح تدفق هواء العلية لدعم أداء السقف والعلية.",
    definition:
      "ترقيات تهوية السقف تحسن مسارات الدخول والخروج حتى تدير العلية الرطوبة والحرارة بفعالية أكبر عندما يكون التوازن الحالي غير كافٍ.",
    metaTitle: "ترقية تهوية السقف مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "ترقيات تهوية السقف والعلية لمنازل مترو فانكوفر. موازنة مدخل السوفيت والعادم لإدارة الرطوبة والحرارة.",
    forWhom: "أصحاب لديهم تكثف علية أو شيخوخة ألواح غير متساوية أو سوفيت مسدود أو عادم غير كافٍ.",
    warningSigns: [
      "تكثف أو صقيع في العلية",
      "روائح عفنة",
      "شيخوخة ألواح غير متساوية",
      "مدخل سوفيت مسدود أو مطلي",
      "علية حارة تساهم في رطوبة",
    ],
    evaluates: [
      "تكوين العلية",
      "فتحات العادم الحالية",
      "استمرارية مسار المدخل",
      "وضع العزل عند الحواف",
      "قيود هندسة السقف",
    ],
    causes: [
      "عادم غير كافٍ",
      "مدخل مسدود",
      "عزل يغطي السوفيت",
      "مراوح حمام/مطبخ تصب في العلية",
      "إعادة تسقيف سابقة بلا تصميم تهوية",
    ],
    methodology: [
      "تقييم توازن المدخل والعادم",
      "تفريغ أو استعادة مدخل السوفيت حيث أمكن",
      "إضافة أو تصحيح مكونات العادم",
      "تجنب زيادة العادم بلا مدخل",
      "التنسيق مع عزل وإحكام هواء عند الحاجة",
    ],
    materials: [
      "فتحات عادم قمة أو صندوقية أو أخرى حسب المواصفة",
      "مكونات مدخل سوفيت",
      "حواجز أو إكسسوارات علية",
      "فلاشينغ للاختراقات الجديدة",
    ],
    bcClimate:
      "طقس بريتيش كولومبيا البارد الرطب يجعل إدارة رطوبة العلية مهمة؛ التهوية جزء من النظام لا علاجاً لكل التسربات.",
    whenAppropriate: "عندما يساهم اختلال تدفق الهواء في رطوبة العلية أو تسريع تآكل الغطاء.",
    whenOtherService: "ترقية التهوية لا تستبدل إصلاح التسرب ولا كل مصادر التكثف.",
    solutionCategories: [
      {
        title: "تطوير مخارج الهواء",
        body: "إضافة فتحات تهوية علوية أو جانبية أو تصحيحها حسب الحاجة.",
      },
      {
        title: "تحسين مداخل الهواء",
        body: "إعادة فتح مسارات دخول الهواء المسدودة عند باطن الحافة أو تحسينها.",
      },
      {
        title: "مراجعة التوازن",
        body: "موازنة دخول الهواء وخروجه بما يتناسب مع حجم مساحة العلية.",
      },
    ],
    repairVsReplace: {
      repair: "ترقية التهوية عند توثيق الاختلال.",
      replace: "لا تعامل الفتحات كبديل عن إصلاحات العزل المائي.",
    },
    scopeFactors: [
      "حجم العلية",
      "أنواع الفتحات الحالية",
      "توفر المدخل",
      "نوع غطاء السقف",
      "الوصول",
    ],
    process: [
      "مراجعة العلية ومكونات التهوية الخارجية",
      "تقييم توازن المدخل والعادم لحجم العلية",
      "نطاق عمل مكتوب للفتحات واستعادة المدخل والحواجز",
      "تركيب وفلاشينغ المكونات الجديدة",
      "التأكد من أن مسارات الهواء غير مسدودة",
    ],
    publicPriceHint:
      "تُحدد مشاريع التهوية بحجم العلية وأنواع الفتحات الحالية وتوفر المدخل وغطاء السقف المعني. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هي ترقية تهوية السقف؟",
        answer:
          "ترقيات تهوية السقف تحسن مسارات الدخول والخروج حتى تدير العلية الرطوبة والحرارة بفعالية أكبر عندما يكون التوازن الحالي غير كافٍ.",
      },
      {
        question: "ما علامات تهوية العلية السيئة؟",
        answer:
          "تكثف أو صقيع على الجانب السفلي للركيزة في الشتاء، روائح عفنة، بقع عفن، صدأ أطراف المسامير وألواح تتقادم بشكل غير متساوٍ. في الساحل البريطاني العرض السائد رطوبة لا حرارة صيفية.",
      },
      {
        question: "هل إضافة فتحات سقف أكثر تصلح تكثف العلية؟",
        answer:
          "ليس وحده. عادم بلا مدخل سوفيت مطابق قد يسحب هواء من المنزل بدل الخارج وأحياناً يزيد الرطوبة. مراوح الحمام المصبّة في العلية وتسرب الهواء من مسكن المعيشة غالباً المحرك الحقيقي، فيُقيّم المسار كاملاً.",
      },
      {
        question: "فتحات القمة أم الصندوقية: ما الفرق؟",
        answer:
          "فتحات القمة تمتد على طول قمة السقف وتعادم بانتظام عبر العلية؛ الصندوقية وحدات منفصلة قرب القمة. أيهما يناسب سقفاً يعتمد على طول القمة والهندسة وتوزيع المدخل.",
      },
      {
        question: "هل تهوية أفضل تطيل عمر الألواح؟",
        answer:
          "تهوية متوازنة تقلل حمل الحرارة والرطوبة في العلية الذي يسرّع شيخوخة الألواح وقد يساهم في مشاكل الركيزة. عامل واحد بين عدة: التعرض وجودة المادة والتركيب ما زالت مهمة، فهي تدعم العمر لا تضمنه.",
      },
    ],
  },
  "fascia-soffit-repair-replacement": {
    title: "إصلاح واستبدال الواجهة والسوفيت",
    shortTitle: "واجهة وسوفيت",
    eyebrow: "مكونات الحافة",
    description:
      "إصلاح أو استبدال الواجهة والسوفيت والمكونات التالفة التي تدعم المزاريب وتفاصيل الحافة.",
    definition:
      "إصلاح أو استبدال الواجهة والسوفيت يعيد مكونات الحافة التي تنهي حافة السقف وتدعم المزاريب وتساهم في مدخل التهوية.",
    metaTitle: "إصلاح الواجهة والسوفيت مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "إصلاح أو استبدال الواجهة والسوفيت لمنازل مترو فانكوفر. استعادة هيكل الحافة والمظهر ومسارات المدخل.",
    forWhom: "أصحاب لديهم ألواح واجهة لينة أو سوفيت فاشل أو انفصال مزاريب أو وصول آفات عند الحواف.",
    warningSigns: [
      "ألواح واجهة لينة أو متعفنة",
      "ألواح سوفيت متدلية",
      "علاقات مزاريب تنفصل",
      "فشل طلاء مع تآكل الركيزة",
      "فتحات يستخدمها الآفات",
    ],
    evaluates: [
      "مدى الضرر الخطي",
      "اختيار المادة",
      "احتياجات تهوية السوفيت",
      "خطة إعادة تثبيت المزاريب",
      "نطاق الطلاء أو التشطيب",
    ],
    causes: [
      "فيض مزاريب يبلل الخشب",
      "معدن حافة فاشل",
      "نهاية عمر المادة الأصلية",
      "رطوبة تهوية",
      "ضرر صدمي أو تجديد",
    ],
    methodology: [
      "فحص وخريطة الأطوال التالفة",
      "إزالة المكونات الفاشلة",
      "تركيب مواد واجهة/سوفيت جديدة",
      "استعادة فتحات التهوية كما صُممت",
      "تنسيق إعادة تركيب المزاريب",
    ],
    materials: [
      "ألواح واجهة أو أنظمة تكسية",
      "ألواح سوفيت",
      "مسامير وإكسسوارات",
      "أنظمة طلاء أو تشطيب عند الإدراج",
    ],
    bcClimate: "فيض مزاريب المزمن في الأشهر الممطرة سبب شائع لتعفن الواجهة في اللوير مينلاند.",
    whenAppropriate: "عندما تكون مكونات الحافة تالفة لكن سطح السقف قد يبقى قابلاً للخدمة.",
    whenOtherService: "الجمع مع عمل مزاريب أو حافة سقف عندما تسببت تلك الأنظمة الضرر أو ستعيده.",
    solutionCategories: [
      {
        title: "إصلاح ألواح الواجهة",
        body: "استبدال الألواح المتعفنة التي تحمل المزاريب والمعدن الطرفي.",
      },
      { title: "إصلاح السوفيت", body: "استعادة ألواح الجزء السفلي وفتحات المدخل." },
      { title: "تنسيق الحافة", body: "تسلسل المزاريب وتفاصيل الحافة مع المكونات الجديدة." },
    ],
    repairVsReplace: {
      repair: "إصلاح موضعي عندما يكون الضرر محدوداً.",
      replace: "استبدال أطوال أطول عندما يكون التعفن مستمراً على الحافة.",
    },
    scopeFactors: [
      "الأمتار الخطية",
      "اختيار المادة",
      "الارتفاع/الوصول",
      "متطلبات التشطيب",
      "تفاعل المزاريب",
    ],
    process: [
      "فحص وخريطة أطوال الحافة التالفة",
      "تخطيط فك وإعادة تركيب المزاريب",
      "نطاق عمل مكتوب باختيار المادة والتشطيب",
      "إزالة المكونات المتعفنة وتركيب جديدة",
      "استعادة فتحات المدخل وإعادة تعليق المزاريب",
    ],
    publicPriceHint:
      "يعتمد عمل الحافة على الأمتار الخطية والارتفاع والوصول واختيار المادة والتشطيب وكيف تتفاعل المزاريب مع الإصلاح. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو إصلاح واستبدال الواجهة والسوفيت؟",
        answer:
          "إصلاح أو استبدال الواجهة والسوفيت يعيد مكونات الحافة التي تنهي حافة السقف وتدعم المزاريب وتساهم في مدخل التهوية.",
      },
      {
        question: "ما الذي يتسبب في تعفن ألواح الواجهة؟",
        answer:
          "التسلسل المحلي المعتاد هو فيض مزاريب أو حافة تقطير فاشلة تبلل اللوح باستمرار خلال الموسم الممطر. عند فشل طبقة الطلاء يبقى الخشب رطباً ويتسارع التعفن. إصلاح مصدر الماء مهم بقدر استبدال اللوح.",
      },
      {
        question: "هل يمكن استبدال الواجهة دون إزالة المزاريب؟",
        answer:
          "نادراً بشكل جيد. المزاريب مثبتة عبر الواجهة، فالاستبدال الصحيح يعني الفك وإعادة التعليق، وهي فرصة لتصحيح الميل والتثبيت في مادة جديدة سليمة.",
      },
      {
        question: "هل السوفيت جزء من نظام تهوية العلية؟",
        answer:
          "في معظم المنازل نعم: ألواح السوفيت تحمل فتحات المدخل التي تغذي تدفق العلية. يجب أن يحافظ الاستبدال على ذلك المدخل؛ حجبه أثناء ترقية تجميلية يخلق رطوبة علية بصمت.",
      },
      {
        question: "خشب أو ألومنيوم أو مركب للواجهة: أيهما أختار؟",
        answer:
          "الخشب تقليدي وقابل للإصلاح لكن يحتاج صيانة تشطيب. تكسية ألومنيوم فوق الخشب تقاوم الطقس لكن قد تحبس رطوبة إن فُصّلت بشكل سيء. المركبات تقاوم التعفن بصيانة أقل. التعرض والميزانية وعمارة المنزل توجّه الاختيار.",
      },
    ],
  },
  "cedar-shake-repair-restoration": {
    title: "إصلاح واستعادة ألواح الأرز",
    shortTitle: "إصلاح أرز",
    eyebrow: "أسقف خشب متميز",
    description: "استبدال ألواح أرز تالفة وتنظيف أو استعادة مناطق محلية على أسقف خشب متميزة.",
    definition:
      "إصلاح ألواح الأرز يعيد ألواحاً وتفاصيل فاشلة معزولة عندما يبقى سقف الأرز الأوسع يستحق الصيانة.",
    metaTitle: "إصلاح ألواح الأرز مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "إصلاح واستعادة موضعية لأسقف أرز في مترو فانكوفر. الحفاظ على أرز قابل للخدمة باستبدال ألواح مستهدف.",
    forWhom: "أصحاب أسقف أرز لديهم ألواح منشقة أو مفقودة أو مقوسة في مناطق محدودة.",
    warningSigns: [
      "ألواح منفردة منشقة أو مفقودة",
      "تقوس موضعي",
      "طحلب يحبس الرطوبة على الخشب",
      "بقع مسامير مع فشل معزول",
      "سدود حطام على أجزاء منخفضة الميل",
    ],
    evaluates: [
      "توفر ألواح مطابقة",
      "حالة سطح السقف المتبقي",
      "احتياجات التنظيف",
      "واجهات الفلاشينغ",
      "هل الاستبدال الكامل أقرب أجل",
    ],
    causes: [
      "دورات أشعة فوق بنفسجية وترطيب",
      "نمو بيولوجي",
      "صدمة",
      "إصلاحات سابقة غير صحيحة",
      "شيخوخة أسقف خشب متميزة ما زالت تستحق صيانة",
    ],
    methodology: [
      "تأمين ألواح مطابقة",
      "استبدال الوحدات الفاشلة بعناية",
      "معالجة الحطام والنمو المحتفظ بالرطوبة",
      "مراجعة الفلاشينغ في منطقة العمل",
      "توثيق مؤشرات العمر المتبقي",
    ],
    materials: [
      "ألواح أرز بدرجة مناسبة",
      "ترقيع طبقة سفلية عند الحاجة",
      "مسامير متوافقة",
      "أساليب تنظيف مناسبة للخشب",
    ],
    bcClimate:
      "مناخ بريتيش كولومبيا الرطب صعب على الأسقف الخشبية؛ الصيانة تمد العمر المفيد عندما يبقى سطح السقف سليماً أساساً.",
    whenAppropriate: "عندما تكون الأعطال معزولة وسطح السقف الأوسع قابل للصيانة.",
    whenOtherService: "تقوس منهجي وتسرب وطبقة سفلية فاشلة يشير لاستبدال أرز كامل.",
    solutionCategories: [
      {
        title: "استبدال ألواح انتقائي",
        body: "استعادة وحدات فاشلة دون التخلي عن سطح سقف قابل للصيانة.",
      },
      { title: "تنظيف موضعي", body: "تقليل حطام ونمو يحبس الرطوبة حول منطقة الإصلاح." },
      { title: "فرز الحالة", body: "فصل أرز قابل للصيانة عن أسقف تحتاج نقاش استبدال كامل." },
    ],
    repairVsReplace: {
      repair: "إصلاح عندما يبقى سطح السقف يستحق الصيانة.",
      replace: "استبدال عندما يكون التآكل منهجياً.",
    },
    scopeFactors: [
      "مساحة الإصلاح",
      "مطابقة الألواح",
      "الوصول",
      "نطاق التنظيف",
      "احتياجات فلاشينغ مجاورة",
    ],
    process: [
      "تقييم سطح السقف والنمو ومواقع الفشل",
      "تأمين ألواح مطابقة بدرجة مناسبة",
      "نطاق عمل مكتوب لمناطق الإصلاح والتنظيف",
      "استبدال الألواح الفاشلة ومعالجة الحطام المحتفظ بالرطوبة",
      "توثيق مؤشرات العمر المتبقي للتخطيط",
    ],
    publicPriceHint:
      "يعكس إصلاح الأرز مساحة الإصلاح ومطابقة الألواح والوصول ونطاق التنظيف واحتياجات الفلاشينغ المجاورة. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو إصلاح واستعادة ألواح الأرز؟",
        answer:
          "إصلاح ألواح الأرز يعيد ألواحاً وتفاصيل فاشلة معزولة عندما يبقى سقف الأرز الأوسع يستحق الصيانة.",
      },
      {
        question: "هل يستحق سقف الأرز الإصلاح أم الاستبدال؟",
        answer:
          "العوامل الحاسمة حالة سطح السقف كاملاً والطبقة السفلية تحته لا أسوأ بضع ألواح. انشقاقات معزولة على سطح سقف سليم وصرف جيد يفضل الإصلاح؛ تقوس واسع وألواح لينة وتسربات متكررة عبر الأسطح يفضل تخطيط الاستبدال.",
      },
      {
        question: "لماذا بعض ألواحي مقوسة ومنشقة؟",
        answer:
          "الأرز يتحرك مع دورات التبليل والتجفيف. المنحدرات المشمسة تجف بقوة وتنشق؛ المظللة الرطبة تبقى رطبة وتتقوس أو تنمو طحلباً. العمر والدرجة والتثبيت الأصلي يؤثرون على متى يظهر ذلك.",
      },
      {
        question: "هل يمكن معالجة الأرز لإطالة عمره؟",
        answer:
          "إبقاء الأرز خالياً من الطحلب والحطام أعلى قيمة صيانة في هذا المناخ لأن الرطوبة المحبوسة تدفع التآكل. معالجات سطحية موجودة وتُناقش حالة بحالة؛ تكمّل الصرف والجفاف لا تستبدلهما.",
      },
      {
        question: "هل ستطابق مناطق الإصلاح لون الأرز الحالي؟",
        answer:
          "الأرز الجديد يبدأ ذهبياً ويتجه للرمادي عبر مواسم التعرض، فالإصلاحات واضحة أولاً وتندمج تدريجياً. عندما يكون المظهر حاسماً، يُناقش الموضع والتوقعات قبل العمل.",
      },
    ],
  },
  "flat-low-slope-roof-repair": {
    title: "إصلاح السقف المسطح ومنخفض الميل",
    shortTitle: "إصلاح مسطح",
    eyebrow: "أغشية منخفضة الميل",
    description:
      "ترقيع أغشية SBS الملحومة باللهب وأنظمة TPO وEPDM، مع إصلاح الفلاشينج ونقاط الاختراق في الأسقف المسطحة ومنخفضة الميل.",
    definition:
      "إصلاح السقف المسطح ومنخفض الميل يعالج درزات الغشاء والفلاشينغ والاختراقات وفشل الغشاء الموضعي دون استبدال نظام السقف بالكامل بالضرورة.",
    metaTitle: "إصلاح السقف المسطح مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "إصلاح أسقف مسطحة ومنخفضة الميل لـ SBS و TPO و EPDM وغيرها في مترو فانكوفر. ترقيع درزات وفلاشينغ واختراقات عند الجدوى.",
    forWhom:
      "أصحاب لديهم تسرب تجمع أو درزات فاشلة أو تسرب حافة أو مناطق غشاء مفتوحة على أسطح منخفضة الميل.",
    warningSigns: [
      "درزات مفتوحة",
      "علامات ثقب",
      "حواف اختراق أو جيوب فاشلة",
      "بثور مع تسرب نشط",
      "مصارف لا تتحكم بالماء",
    ],
    evaluates: [
      "نوع الغشاء",
      "توافق الترقيع",
      "أنماط الصرف",
      "حالة الاختراق",
      "هل العزل رطب خارج الإصلاح المحلي",
    ],
    causes: [
      "شيخوخة الغشاء",
      "ضرر مشي",
      "إجهاد تجمع الماء",
      "حركة فلاشينغ عند الدرابزين",
      "ترقيعات سابقة غير متوافقة",
    ],
    methodology: [
      "تحديد نظام الغشاء",
      "تحديد طريقة ترقيع متوافقة",
      "إصلاح فلاشينغ واختراقات",
      "تحسين تفاصيل صرف محلية حيث عملي",
      "التوصية بالاستبدال إن استنفد النظام",
    ],
    materials: [
      "ترقيعات غشاء متوافقة",
      "معادن فلاشينغ وإكسسوارات",
      "جيوب اختراق أو تفاصيل جاهزة",
      "لاصقات أو عمليات حرارية حسب النظام",
    ],
    bcClimate:
      "مواسم رطبة طويلة ومخاطر تجمع الماء تجعل تفاصيل الدرز والمصرف حاسمة على أسقف اللوير مينلاند المنخفضة الميل.",
    whenAppropriate: "عندما تكون الأعطال موضعية ويستطيع سطح الغشاء قبول رقع دائمة ومتوافقة.",
    whenOtherService: "عزل مشبع أو فشل درز منهجي عادة يعني تخطيط استبدال.",
    solutionCategories: [
      {
        title: "ترقيع الغشاء",
        body: "إصلاحات متوافقة للدرزات والثقوب والضرر الموضعي في سطح الغشاء.",
      },
      { title: "تفاصيل الاختراق والحافة", body: "استعادة العزل المائي عند فتحات السطح." },
      { title: "تصحيحات تركز على الصرف", body: "معالجة تفاصيل تحبس الماء عند نقاط ضعيفة." },
    ],
    repairVsReplace: {
      repair: "إصلاح عندما يبقى الغشاء قابلاً للخدمة على نطاق واسع.",
      replace: "استبدال عندما يكون النظام مشبعاً أو يفشل باستمرار.",
    },
    scopeFactors: ["نوع الغشاء", "مساحة الإصلاح", "الوصول", "عدد الاختراقات", "ظروف الصرف"],
    process: [
      "تحديد نظام الغشاء ونقاط الفشل",
      "مراجعة رطوبة وصرف حول الضرر",
      "نطاق عمل مكتوب لترقيع وتفاصيل متوافقة",
      "تنفيذ إصلاحات بأساليب مناسبة للنظام",
      "مراجعة سلوك الصرف وتوثيق العمل",
    ],
    publicPriceHint:
      "يعتمد إصلاح السقف المسطح على نوع الغشاء ومساحة الإصلاح وعدد الاختراقات وظروف الصرف والوصول. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو إصلاح السقف المسطح ومنخفض الميل؟",
        answer:
          "إصلاح السقف المسطح ومنخفض الميل يعالج درزات الغشاء والفلاشينغ والاختراقات وفشل الغشاء الموضعي دون استبدال نظام السقف بالكامل بالضرورة.",
      },
      {
        question: "لماذا يهم نوع الغشاء للترقيع؟",
        answer:
          "لكل غشاء طريقة تركيب مختلفة: غشاء SBS من البيتومين المعدّل يُثبت باللهب أو بالمواد اللاصقة، ويُلحم TPO حرارياً، بينما يعتمد EPDM على اللواصق والأشرطة. استخدام مادة أو طريقة غير مناسبة يعطي رقعة قصيرة العمر، وهو سبب شائع لتكرار التسرّب.",
      },
      {
        question: "هل تجمع الماء على سقفي المسطح مشكلة؟",
        answer:
          "الماء الراكد الذي يبقى بعد المطر يجهد الدرزات ويسرّع شيخوخة الغشاء ويكبّر أي عيب صغير. بعض الأسباب قابلة للإصلاح: مصارف مسدودة، منخفضات موضعية، بينما مشاكل الميل الإنشائي تشير لنقاش مستوى الاستبدال.",
      },
      {
        question: "هل يمكن إصلاح مسطح في الشتاء؟",
        answer:
          "غالباً نعم مع تعديلات الطريقة: بعض المواد واللاصقات لها حدود حرارة ويجب أن تكون الأسطح جافة. التثبيت الطارئ شبه دائماً ممكن؛ بعض التفاصيل الدائمة قد تنتظر نافذة طقس مناسبة.",
      },
      {
        question: "كيف أعرف إن كان العزل تحت الغشاء رطباً؟",
        answer:
          "أعراض تشمل رطوبة داخلية مستمرة وتنفخ الغشاء ومناطق طرية تحت القدم؛ التأكيد قد يحتاج مسح رطوبة أو قطع اختبار. العزل الرطب مهم لأن الترقيع فوقه يحبس الرطوبة ويواصل التدهور.",
      },
    ],
  },
  "metal-roof-repair-maintenance": {
    title: "إصلاح وصيانة السقف المعدني",
    shortTitle: "إصلاح معدني",
    eyebrow: "أنظمة معدنية",
    description:
      "إصلاح مسامير وفلاشينغ ومادة مانعة للتسرّب وألواح واختراقات لأنظمة التسقيف المعدنية.",
    definition:
      "إصلاح وصيانة السقف المعدني يصحح المسامير والفلاشينغ والمادة المانعة للتسرّب وضرر الألواح والاختراقات مع الحفاظ على ألواح قابلة للخدمة.",
    metaTitle: "إصلاح السقف المعدني مترو فانكوفر | رولين روفينغ",
    metaDescription:
      "إصلاح وصيانة أسقف معدنية لمنازل مترو فانكوفر. مسامير وفلاشينغ وإغلاقات وتصحيح اختراقات على أنظمة موجودة.",
    forWhom:
      "أصحاب لديهم مسامير مرتخية أو إغلاقات فاشلة أو تسرب اختراق أو ضرر لوح معزول على أسقف معدنية.",
    warningSigns: [
      "مسامير بارزة على أنظمة ظاهرة",
      "إغلاقات رغوية فاشلة",
      "تسرب طوق اختراق",
      "تشقق مادة مانعة للتسرّب عند التفاصيل",
      "صدأ حافة مقطوعة عند الفلاشينغ",
    ],
    evaluates: [
      "ملف اللوح",
      "حالة الطلاء",
      "نوع المسمار",
      "تفاصيل الإغلاق والفلاشينغ",
      "هل الضرر منهجي",
    ],
    causes: [
      "دورات حرارية",
      "نوع مسمار خاطئ",
      "شيخوخة المادة المانعة للتسرّب",
      "انبعاج صدمي",
      "تأثيرات التعرض الساحلي",
    ],
    methodology: [
      "تحديد نوع النظام",
      "تصحيح مسامير وإغلاقات حسب الحاجة",
      "إصلاح فلاشينغ واختراقات",
      "معالجة ضرر لوح معزول",
      "تجنب إصلاحات مادة مانعة للتسرّب تجميلية حيث يلزم تفصيل معدني",
    ],
    materials: [
      "مسامير وصواميل متوافقة",
      "شرائط إغلاق",
      "معادن فلاشينغ",
      "أنظمة لمسة طلاء فقط عند الملاءمة",
    ],
    bcClimate:
      "التعرض الساحلي والحركة الحرارية يجعلان المسامير والفلاشينغ نقاط صيانة متكررة على الأسقف المعدنية.",
    whenAppropriate: "عندما تبقى الألواح سليمة والمشاكل على مستوى المكونات.",
    whenOtherService: "فشل طلاء منهجي أو ملفات قديمة قد يقود لنقاش استبدال.",
    solutionCategories: [
      { title: "صيانة مسامير وإغلاق", body: "استعادة تفاصيل ميكانيكية محكمة." },
      { title: "إصلاح فلاشينغ واختراق", body: "تصحيح واجهات تدخل الماء." },
      {
        title: "إصلاح موضعي للألواح",
        body: "معالجة الضرر المحدود مع الحفاظ على بقية السطح السليم.",
      },
    ],
    repairVsReplace: {
      repair: "صيانة وإصلاح عندما يبقى سطح السقف المعدني قابلاً للخدمة.",
      replace: "استبدال عندما لا يعود النظام قابلاً للصيانة اقتصادياً أو تقنياً.",
    },
    scopeFactors: ["نوع النظام", "الوصول", "عدد التفاصيل", "حالة الطلاء", "كثافة الاختراقات"],
    process: [
      "تحديد نظام الألواح ونوع التثبيت",
      "فحص المسامير والإغلاقات والفلاشينغ والاختراقات",
      "نطاق عمل مكتوب يفصل الصيانة عن الإصلاح",
      "تصحيح التفاصيل بمكونات متوافقة",
      "مراجعة السقف كنظام عامل",
    ],
    publicPriceHint:
      "يُحدد عمل السقف المعدني بنوع النظام وعدد التفاصيل التي تحتاج تصحيحاً وحالة الطلاء والوصول. اطلب تقييماً.",
    faqs: [
      {
        question: "ما هو إصلاح وصيانة السقف المعدني؟",
        answer:
          "إصلاح وصيانة السقف المعدني يصحح المسامير والفلاشينغ والمادة المانعة للتسرّب وضرر الألواح والاختراقات مع الحفاظ على ألواح قابلة للخدمة.",
      },
      {
        question: "لماذا تبرز مسامير سقفي المعدني؟",
        answer:
          "أنظمة المسامير الظاهرة تعتمد على مسامير مطاطية تتحرك مع الدورات الحرارية؛ مع السنوات قد ترتخي وتتصلب الحشوات. تصحيحها، أحياناً ببدائل أكبر، صيانة طبيعية لهذه الأنظمة لا علامة فشل السقف.",
      },
      {
        question: "سقفي المعدني يتسرب عند أنبوب تهوية: هل هذا شائع؟",
        answer:
          "تُعد نقاط الاختراق من أكثر مواضع التسرّب شيوعاً في الأسقف المعدنية. تتقادم عوازل الأنابيب والأطواق أسرع من الألواح ويجب أن تستوعب حركتها. وغالباً يمكن استبدال العازل بتفصيل متوافق من دون فتح بقية سطح السقف.",
      },
      {
        question: "هل يمكن إصلاح لوح معدني منبعج؟",
        answer:
          "الانبعاجات التجميلية التي لا تضر بالطلاء أو الدرزات غالباً يمكن تركها. الضرر الذي يكسر الطلاء أو يفتح درزاً أو يحبس الماء يبرر إصلاح أو استبدال الورقة المتأثرة، وهو عملي على معظم الملفات.",
      },
      {
        question: "كيف تختلف صيانة البدرز الواقف عن المعدن بالمسامير؟",
        answer:
          "البدرز الواقف يخفي التثبيت على مشابك، فصيانة الحشوات الروتينية تختفي تقريباً؛ الاهتمام ينتقل للفلاشينغ والاختراقات ووصلات المادة المانعة للتسرّب عند النهايات. أنظمة المسامير الظاهرة تحتاج فحص مسامير دورياً كأمر معتاد.",
      },
    ],
  },
  "cedar-shake-roof-replacement": {
    title: "استبدال سقف ألواح الأرز",
    shortTitle: "استبدال أرز",
    eyebrow: "إعادة تسقيف خشب متميز",
    description: "إعادة تسقيف أرز كاملة متميزة للمنازل حيث التسقيف الخشبي خيار معماري مقصود.",
    definition:
      "استبدال سقف ألواح الأرز هو إعادة تسقيف كاملة بألواح أرز أو مكونات خشب محددة، بما في ذلك تحضير الركيزة والتفصيل الصحيح لنظام خشب متميز.",
    metaTitle: "استبدال سقف ألواح الأرز فانكوفر | رولين روفينغ",
    metaDescription:
      "استبدال كامل لسقف ألواح أرز لمنازل مترو فانكوفر. إعادة تسقيف خشب متميز مع تحضير ركيزة وتفصيل صحيح.",
    forWhom: "أصحاب يلتزمون بتجديد طابع الخشب عندما يجعل التآكل المنهجي للأرز الإصلاح غير متماسك.",
    warningSigns: [
      "تقوس وتشقق منهجي",
      "تسربات متكررة عبر الأسطح",
      "فشل طبقة سفلية تحت الأرز",
      "مخاوف بيع أو تخطيط حول العمر المتبقي",
      "نية معمارية للإبقاء على التسقيف الخشبي",
    ],
    evaluates: [
      "درجة وملف الألواح",
      "استراتيجية الطبقة السفلية",
      "تهوية وجفاف",
      "اعتبارات الحريق وسياق المشروع",
      "تعقيد الهندسة",
    ],
    causes: [
      "اكتمال العمر الافتراضي",
      "رطوبة مزمنة",
      "خسائر عاصفة عبر عدة أسطح",
      "مشاكل ركيزة مكتشفة مع الوقت",
    ],
    methodology: [
      "تقييم الركيزة واحتياجات نظام السقف",
      "تحديد نظام ألواح وطبقة سفلية",
      "حماية العقار أثناء الإزالة",
      "تركيب كامل سطح الألواح الخشبية وتفاصيله كنظام سقف متكامل",
      "مراجعة توقعات صيانة الأرز",
    ],
    materials: [
      "ألواح أرز بدرجة محددة",
      "أنظمة طبقة سفلية",
      "فلاشينغ متوافق مع الأسقف الخشبية",
      "مسامير وتفاصيل قمة",
    ],
    bcClimate:
      "أسقف الخشب المتميزة في بريتيش كولومبيا تحتاج توقعات صيانة صادقة؛ الاستبدال يعيد نظام السقف عندما لا يواكب الإصلاح التآكل المنهجي.",
    whenAppropriate:
      "عندما يريد المالك نظام أرز كاملاً والسقف الخشبي الحالي قرب نهاية خدمته المتماسكة.",
    whenOtherService: "فشل معزول على سطح سقف لا يزال قوياً قد يناسب إصلاح أرز.",
    solutionCategories: [
      { title: "تركيب سطح كامل من ألواح الأرز", body: "ألواح جديدة كنظام سقف متماسك." },
      {
        title: "تحضير الركيزة والطبقة السفلية",
        body: "معالجة الركيزة وطبقات تصريف الماء تحت الخشب.",
      },
      { title: "تفصيل للأسقف الخشبية", body: "فلاشينغ وحواف مفصّلة لأداء الأرز." },
    ],
    repairVsReplace: {
      repair: "إصلاح إن كان معزولاً.",
      replace: "إعادة تسقيف أرز كاملة عندما يكون التآكل منهجياً والخشب لا يزال النظام المختار.",
    },
    scopeFactors: ["مساحة السقف", "مواصفة الألواح", "إصلاحات الركيزة", "الوصول", "تعقيد المشروع"],
    process: [
      "تقييم السقف الخشبي الحالي والركيزة",
      "تحديد درجة الألواح والطبقة السفلية والتفاصيل",
      "نطاق عمل مكتوب وخطة حماية العقار",
      "إزالة وتحضير ركيزة وتركيب",
      "مراجعة إنجاز وإرشاد صيانة",
    ],
    publicPriceHint:
      "يعكس سعر إعادة تسقيف الأرز مساحة السقف والهندسة ومواصفة الألواح وإصلاحات الركيزة والوصول. اطلب تقييماً للحصول على نطاق عمل مكتوب.",
    faqs: [
      {
        question: "ما هو استبدال سقف ألواح الأرز؟",
        answer:
          "استبدال سقف ألواح الأرز هو إعادة تسقيف كاملة بألواح أرز أو مكونات خشب محددة، بما في ذلك تحضير الركيزة والتفصيل الصحيح لنظام خشب متميز.",
      },
      {
        question: "كم يدوم سقف أرز في بريتيش كولومبيا؟",
        answer:
          "العمر يختلف بشكل كبير حسب درجة الألواح والتعرض والتفصيل وفوق كل ذلك الصيانة، فرقم واحد سيكون مضللاً. أسقف أرز مبنية وصُونت جيداً في المنطقة تخدم عادة عقوداً؛ المهملة تفشل أبكر بكثير. توقعات واقعية لمنحدراتك وظلك جزء من التقييم.",
      },
      {
        question: "ما الفرق بين ألواح الأرز والقشور الخشبية؟",
        answer:
          "الألواح منشقة وأسمك بوجه محكم هو ما يتخيله معظم الناس للأسقف الخشبية المتميزة؛ القشور منشورة وأرق وأكثر انتظاماً. الاختيار يؤثر على المظهر والتفصيل والتكلفة ويُحسم أثناء المواصفة.",
      },
      {
        question: "هل يحتاج سقف الأرز طبقة سفلية خاصة؟",
        answer:
          "الأسقف الخشبية تحتاج استراتيجية طبقة سفلية تصرف الماء مع السماح للأرز بالجفاف لأن الأرز يؤدي ضعيفاً عندما لا يتنفس. الطبقة بين الصفوف والحماية عند الحافة عناصر نموذجية لنظام سقف صحيح.",
      },
      {
        question: "هل الأرز لا يزال خياراً معقولاً في مترو فانكوفر؟",
        answer:
          "للمنزل المناسب يبقى خياراً متميزاً مقصوداً: العمارة وطابع الحي والتزام المالك بالصيانة كلها مهمة. المناخ الرطب يتطلب توقعات صيانة صادقة؛ من يفضلون صيانة قليلة عادة يُخدَمون بنظام متميز آخر.",
      },
    ],
  },
} as const satisfies Record<ServiceSlug, ServiceContentTranslation>;

export const serviceAreas = {
  vancouver: {
    intro:
      "منازل فانكوفر متنوعة من وحدات صغيرة إلى منازل مصممة بإضافات وشرفات وأجزاء منخفضة الميل. يبدأ تقييم السقف المفيد بالشكل الفعلي للمبنى لا بتوصية نظام واحد للجميع.",
    localContext:
      "قد تخضع المشاريع داخل مدينة فانكوفر لمتطلبات تختلف عن بقية بريتيش كولومبيا. وتتحقق رولين من الجهة المختصة بالعقار ومتطلبات المشروع قبل اعتماد نطاق العمل.",
    assessmentFocus: [
      "الانتقال بين السقف الأصلي والإضافات والأجزاء منخفضة الميل",
      "الوديان وتقاطعات الجدران والاختراقات والفلاشينغ المحيط",
      "مسار الصرف في مواقع حضرية مكتظة",
    ],
    propertyContexts: ["منزل عائلي", "منزل مجدد وموسع", "نظام سقف مائل مختلط"],
    faqs: [
      {
        question: "هل مشروع سقف فانكوفر يتبع نفس قواعد بقية بريتيش كولومبيا؟",
        answer:
          "ليس دائماً. لمدينة فانكوفر نظام بلدية بناء خاص. تُؤكد المتطلبات حسب العنوان والنطاق أثناء التخطيط لا بافتراض من قائمة إقليمية.",
      },
      {
        question: "هل قد يحتاج منزل واحد لأكثر من نهج سقف؟",
        answer:
          "نعم. الإضافات والمداخل وشرفات السقف تقدم ميلاً وانتقالات مختلفة. يعتبر التقييم هذه الوصلات جزءاً من نظام إدارة الماء واحد.",
      },
    ],
  },
  burnaby: {
    intro:
      "الأحياء السكنية في بيرنابي تشمل أراضياً مسطحة وشوارع منحدرة ومنازل نمت بإضافات. هذا التنوع يجعل الصرف والوصول الآمن والوصل بين أجزاء السقف جزءاً مهماً من التقييم الأول.",
    localContext:
      "سقف بيرنابي قد يعمل بشكل مختلف عن سقف على بعد أحياء قليلة حسب التعرض والغطاء الشجري والميل وشكل المبنى. تُكتب التوصيات بناءً على ما يُلاحظ في العقار.",
    assessmentFocus: [
      "الميل والوصول وقيود مناولة المواد حول المنزل",
      "صرف الوديان والأفاريز والمزاريب والأجزاء منخفضة الميل",
      "وصل حيث تلتقي الإضافة بالمبنى الأصلي",
    ],
    propertyContexts: ["عقارات على منحدر", "منازل بإضافات", "مواقع سكنية ناضجة"],
    faqs: [
      {
        question: "لماذا يهم وصول الموقع لنطاق التسقيف؟",
        answer:
          "الوصول يؤثر على الحماية ونقل المواد وترتيب العمل. يجب مراجعته قبل إنهاء خطة التركيب والنطاق.",
      },
      {
        question: "هل يجب مراجعة المزاريب مع السقف؟",
        answer:
          "يُعتبر الصرف مساراً متصلاً من سطح السقف إلى نقطة تصريف مناسبة. يوثق التقييم حالة المزاريب وأنابيب المطر المرئية دون افتراض عمل مزاريب اختياري.",
      },
    ],
  },
  coquitlam: {
    intro:
      "عقارات كوكويتلام قد تجمع مواقع منحدرة ومنازل متعددة المستويات وغطاءً نباتياً قريباً. نراجع حالة السقف ومسار الحطام والصرف وتعرض المطر الهوائي معاً قبل التوصية بالإصلاح أو الاستبدال.",
    localContext:
      "الارتفاع والتعرض في كوكويتلام قد يتغيران بين الأحياء القريبة. لذا يعتمد النظام والتفاصيل المناسبة على المبنى الفردي لا وصفة مواد على مستوى المدينة.",
    assessmentFocus: [
      "صرف السقف السفلي من المستوى العلوي في منزل متعدد الطوابق",
      "الوديان والانتقال من السقف إلى الجدار في أشكال معقدة",
      "تراكم الحطام حول المزاريب والفتحات ونقاط التصريف",
    ],
    propertyContexts: ["منازل متعددة الطوابق", "سكن منحدر", "منازل قرب غطاء نباتي ناضج"],
    faqs: [
      {
        question: "هل السقف المعقد يتطلب استبدالاً تلقائياً؟",
        answer:
          "لا. التعقيد يؤثر على التشخيص والتفاصيل، لكن الإصلاح والاستبدال يعتمدان على ما إذا كانت المشكلة معزولة أو عبر النظام.",
      },
      {
        question: "كيف يُؤخذ حطام الأشجار في الاعتبار أثناء الفحص؟",
        answer:
          "يوثق التقييم المناطق التي يتراكم فيها الحطام أو يبطئ الصرف. ثم ترتبط توصية الصيانة بما يُرى في ذلك المبنى.",
      },
    ],
  },
  surrey: {
    intro:
      "تضم سَري أحياء سكنية متنوعة، من المناطق القديمة إلى المنازل الحديثة ذات الأسقف الكبيرة والمتصلة. وتؤثر مساحة السقف ومدى تعرضه للطقس وعدد نقاط الانتقال في نطاق العمل الفعلي.",
    localContext:
      "لا ينبغي اعتبار التوصية المناسبة لعقار معين مواصفة ثابتة لعقار آخر. توثق رولين حالة السقف المعني وتشرح مزايا كل خيار وحدوده.",
    assessmentFocus: [
      "أسطح كبيرة وكيف يتدفق الماء بينها",
      "الفلاشينغ عند النوافذ السقفية والجدران والمداخن والاختراقات",
      "هل الضرر معزول أم متكرر عبر نظام السقف",
    ],
    propertyContexts: ["منزل عائلي كبير", "سقف حديث متعدد المستويات", "منازل أحياء قديمة"],
    faqs: [
      {
        question: "ما الذي يحدد الإصلاح والاستبدال للسقف الكبير؟",
        answer:
          "ليس الحجم وحده. يعتمد القرار على حالة الغطاء وعدد الأعطال وموقعها وتفاصيل الانتقال وإمكانية إصلاح موضعي دائم.",
      },
      {
        question: "هل يمكن تقييد التقييم بمنطقة مشكلة واحدة؟",
        answer:
          "تُفحص المشكلة المبلغ عنها بدقة، لكن يجب مراجعة الأسطح المجاورة ومسار الصرف لأن الماء يتحرك قبل الظهور داخلياً.",
      },
    ],
  },
  richmond: {
    intro:
      "البيئة المنخفضة عموماً في ريتشموند تجعل صرف السقف المضبوط بارزاً خاصة في فترات الأمطار الطويلة. الأجزاء منخفضة الميل والحواف المحيطة ومسار التصريف تستحق نفس عناية الغطاء الأساسي.",
    localContext:
      "التعرض الساحلي وانفتاح الموقع قد يؤثر على كيفية وصول المطر الهوائي لحواف السقف والانتقالات. تُحدد توصيات المواد والتفاصيل فقط بعد مراجعة ميل وشكل وتعرض المنزل.",
    assessmentFocus: [
      "صرف جيد ونقاط منخفضة مرئية على السطح منخفض الميل",
      "حواف وفلاشينغ وانتقالات معرضة لأمطار غزيرة",
      "كيف تستقبل المزاريب والأنابيب العمودية الماء من سطح السقف",
    ],
    propertyContexts: ["أجزاء سقف منخفضة الميل", "سكن مفتوح", "منزل عائلي حديث"],
    faqs: [
      {
        question: "هل السقف الذي يبدو مسطحاً مسطح فعلاً؟",
        answer:
          "السقف منخفض الميل يجب أن يوجه الماء نحو المصارف. يبحث التقييم عن مسار الصرف المصمم وعلامات أن الماء لا يتحرك كما يُقصد.",
      },
      {
        question: "هل التعرض الساحلي يعني أن المعدن دائماً الخيار الصحيح؟",
        answer:
          "لا. ميل السقف وتصميم نظام السقف وملاءمة المنتج والتفاصيل وأولوية المالك كلها مهمة. لا تُختار المادة من الموقع فقط.",
      },
    ],
  },
  "north-vancouver": {
    intro:
      "منازل فانكوفر الشمالية غالباً في بيئة منحدرة رطبة وكثيفة النبات. الوصول الآمن والجريان المركز والحطام حول نقاط الصرف اعتبارات عملية قبل تحديد نطاق عمل السقف.",
    localContext:
      "تختلف الظروف بين الأحياء المنخفضة والعقارات الأقرب إلى الجبل في نورث شور. يوثّق التقييم درجة التعرّض وإمكانية الوصول الفعلية في الموقع بدلاً من الاكتفاء بوصف عام للمنطقة.",
    assessmentFocus: [
      "الوصول الآمن لأجزاء السقف الحادة أو العالية",
      "مسار صرف كبير بين مستويات السقف",
      "الحطام واحتباس الرطوبة حول السقف المظلل",
    ],
    propertyContexts: ["عقارات حادة", "منزل متعدد المستويات", "عقارات سكنية غابية"],
    faqs: [
      {
        question: "هل قد يحتاج السقف المظلل خطة صيانة مختلفة؟",
        answer:
          "نعم. تختلف سرعة جفاف السقف ومقدار تراكم الحطام بحسب اتجاهه وكثافة الأشجار المحيطة؛ لذلك يجب أن تستند خطة الصيانة إلى حالة كل جزء من السقف.",
      },
      {
        question: "كيف يؤثر الموقع المنحدر على المشروع؟",
        answer:
          "يؤثر على الوصول والحماية وترتيب العمل. تُوثق هذه القيود قبل إنهاء النطاق لا كمفاجأة أثناء العمل.",
      },
    ],
  },
  "west-vancouver": {
    intro:
      "سكن فانكوفر الغربية غالباً يجمع كتلة معمارية معقدة وزجاجاً واسعاً وشرفات وأشكال سقف حادة ومنخفضة الميل. يعتمد نجاح عمل السقف على حل الانتقالات دون فقدان رؤية المبنى.",
    localContext:
      "العقارات المطلة على البحر أو على التل أو الغابية قد تعاني تعرضاً مختلفاً في نفس البلدية. تقيّم رولين شكل السقف والوصول ومسار الماء قبل التوصية بالنظام.",
    assessmentFocus: [
      "الوصل بين المعدن المعماري والغشاء والأجزاء المائلة",
      "الدرابزين وشرفة السقف وواجهة الجدار والصرف المخفي",
      "الوصول وخطة الحماية حول المساحة الخارجية المكتملة",
    ],
    propertyContexts: ["منزل مصمم معمارياً", "إقامة على منحدر", "نظام سقف مواد مختلطة"],
    faqs: [
      {
        question: "هل يمكن لاستبدال السقف الحفاظ على الطابع المعماري للمنزل؟",
        answer:
          "يجب أن يكون جزءاً من التوصية. يمكن تقييم الملف واللون وتفاصيل الحافة والانتقال البصري مع الملاءمة الفنية.",
      },
      {
        question: "لماذا تُقيَّم أنظمة السقف المختلطة معاً؟",
        answer:
          "الماء يعبر حدود مناطق السقف. التفاصيل المناسبة لسطح واحد يجب أن تتصل بموثوقية بنظام السقف التالي.",
      },
    ],
  },
  "new-westminster": {
    intro:
      "نيوويستمنستر تشمل منازل قديمة ومجددة ومواقع سكنية مضغوطة قد يكون فيها السقف قد تغير عبر أجيال. العمل الأول فهم نظام السقف الحالي وأين تتصل التغييرات اللاحقة.",
    localContext:
      "العمر وحده لا يحدد حالة السقف. تجد رولين أدلة في الغطاء والفلاشينغ والانتقالات ونقاط الصرف ثم تميز التفاصيل القابلة للإصلاح من مشكلة النظام الواسعة.",
    assessmentFocus: [
      "طبقات إصلاح أو تجديد سابقة مرئية على سطح السقف",
      "انتقالات الإضافات والمداخل والهياكل الملحقة",
      "وصول المواد وحماية الجيران في موقع مضغوط",
    ],
    propertyContexts: ["منزل عائلي قديم", "منزل مجدد", "موقع سكني كثيف"],
    faqs: [
      {
        question: "هل المنزل القديم يحتاج دائماً سقفاً كاملاً؟",
        answer:
          "لا. الحالة المرئية وأداء نظام السقف أهم من عمر المنزل. يلزم تقييم موثق قبل القرار.",
      },
      {
        question: "ماذا لو كان هناك عدة ترقيعات سابقة على السقف؟",
        answer:
          "يُوثق العمل السابق كجزء من التشخيص. الإصلاح المتكرر في منطقة واحدة قد يشير لانتقال غير محلول؛ الترقيعات غير المرتبطة قد يكون لها سبب آخر.",
      },
    ],
  },
  "port-coquitlam": {
    intro:
      "منازل بورت كوكويتلام غالباً تجمع سقفاً مائلاً مستقيماً مع فضاء خارجي أو مرآب أو إضافات أو أسقف أُضيفت لاحقاً. هذه الأجزاء الفرعية تغير كيف يصل الماء للوديان والجدران والمزاريب.",
    localContext:
      "التسرب قرب الإضافة لا يبدأ بالضرورة مباشرة فوقه. يتتبع التقييم الأسطح والانتقالات قبل تحديد منطقة الإصلاح.",
    assessmentFocus: [
      "الوصل بين السقف الرئيسي والمرآب والإضافة اللاحقة",
      "جزء السقف السفلي الذي يستقبل الماء من السطح العلوي",
      "الاختراق والفلاشينغ عند وصل السقف بالجدار",
    ],
    propertyContexts: ["منزل بمرآب متصل", "منزل عائلي موسع", "صرف سقف متعدد المراحل"],
    faqs: [
      {
        question: "لماذا قد يظهر التسرب بعيداً عن المصدر؟",
        answer:
          "قد يتحرك الماء على الغطاء أو الهيكل أو انتقال السقف قبل الظهور. التشخيص يتبع المسار المحتمل لا يعالج البقعة الداخلية كسبب فقط.",
      },
      {
        question: "هل تُدرج أسقف المرآب والمدخل في التقييم؟",
        answer:
          "قد تُدرج، خاصة إذا كانت متصلة بالسقف الرئيسي أو تصرف منه. يجب أن يحدد نطاق التقييم المطلوب المناطق المراد مراجعتها.",
      },
    ],
  },
  "port-moody": {
    intro:
      "البيئة السكنية الغابية المطلة على المدخل في بورت مودي قد تضع رطوبة وحطاماً وتعرضاً متغيراً على جوانب مختلفة من نفس السقف. يجب أن يميز ملاحظ الفحص هذه الظروف حسب سطح السقف.",
    localContext:
      "اتجاه السقف والغطاء النباتي القريب وارتفاع المبنى قد يكون بنفس أهمية اسم المادة. توثق رولين هذه المتغيرات قبل مناقشة الصيانة أو الإصلاح أو الاستبدال.",
    assessmentFocus: [
      "المطر الهوائي عند الحافة والجدار والاختراق",
      "جمع الحطام في الوديان ونقاط الصرف",
      "أنماط تآكل مختلفة على السطح المظلل والمكشوف",
    ],
    propertyContexts: ["عقارات مطلة على المدخل", "مواقع غابية", "منزل عائلي متعدد المستويات"],
    faqs: [
      {
        question: "هل يمكن أن تتقدم في العمر جوانب مختلفة من سقف واحد بشكل مختلف؟",
        answer:
          "نعم. تختلف الشمس والظل والرياح والحطام حسب الاتجاه. يجب أن يصف تقرير الحالة كل منطقة سقف لا حالة واحدة للكل.",
      },
      {
        question: "هل حطام السطح دليل على تلف السقف؟",
        answer:
          "لا. إنه ملاحظة صيانة وصرف لا تشخيصاً بديلاً. يجب تقييم الغطاء والتفاصيل الأساسية أيضاً.",
      },
    ],
  },
  "maple-ridge": {
    intro:
      "ميبل ريدج تشمل عقارات سكنية كبيرة وأحياء غابية ومواقع على التل حيث قد يختلف التعرض والوصول بشكل كبير. يُراجع السقف والظروف المحيطة كجزء من خطة مشروع واحدة.",
    localContext:
      "في المناطق الأعلى أو الأكثر تعرضاً قد تظهر ظروف لا تمثل المناطق المنخفضة. لذا يرتبط اختيار النظام والتفاصيل بالعقار لا بافتراض إقليمي.",
    assessmentFocus: [
      "الوصول والتحضير والحماية للموقع الأكبر أو المنحدر",
      "إدارة الصرف والحطام قرب الغطاء النباتي الناضج",
      "التعرض عند الحافة العلوية والأفاريز وحافة السقف",
    ],
    propertyContexts: ["مواقع سكنية أكبر", "مناطق على التل", "عقارات غابية"],
    faqs: [
      {
        question: "هل يغير العقار الأكبر توصية السقف؟",
        answer:
          "ليس بذاته. قد يغير الوصول ولوجستيات المشروع، لكن توصية نظام السقف ما زالت تعتمد على الميل ونظام السقف والتعرض والحالة.",
      },
      {
        question: "كيف تُراجع حواف السقف الأكثر تعرضاً؟",
        answer:
          "يوثق التقييم حالة الحافة المرئية ومشاكل التثبيت وتفاصيل الانتقال ثم يربطها بالنظام المختار والمتطلبات المعمول بها.",
      },
    ],
  },
  delta: {
    intro:
      "مجتمعات دلتا تشمل مناطق ساحلية وزراعية وسكنية داخلية. هذا التنوع في التعرض يجعل تقييم الرياح والصرف وشكل السقف في كل موقع حاسماً قبل اختيار الحل.",
    localContext:
      "لا ينبغي أن تتلقى عقارات تساواسن أو لادنر أو نورث دلتا توصيات بناءً على اسم البلدية فقط. تحدد رولين النطاق حسب المبنى والظروف الملحوظة في العنوان.",
    assessmentFocus: [
      "تعرض الرياح عند الأفاريز والحافة العلوية والتفاصيل المحيطة",
      "سعة الصرف والتصريف على الأسطح الواسعة",
      "حالة المواد على واجهات بتعرض مختلف",
    ],
    propertyContexts: ["مناطق ساحلية", "سكن مفتوح", "منزل عائلي داخلي"],
    faqs: [
      {
        question: "هل نظام سقف واحد يناسب كل دلتا؟",
        answer:
          "لا يمكن اختيار المدينة تلقائياً. الميل والتعرض والعمارة والصرف والميزانية كلها تؤثر على النظام المناسب.",
      },
      {
        question: "ماذا نفحص بعد عاصفة رياح؟",
        answer:
          "يمكن لمراجعة مستهدفة فحص تغيرات مرئية على حواف السقف المتاحة والغطاء والفلاشينغ والاختراقات. السلامة أولاً؛ لا يصعد المالك على سقف مبلل أو تالف.",
      },
    ],
  },
  langley: {
    intro:
      "عقارات لانغلي قد تشمل منازل عائلية أكبر ونطاق سقف واسعاً وهياكل ملحقة متعددة. النطاق الواضح يفصل كل منطقة سقف ويحل كيفية الصرف والوصل.",
    localContext:
      "المواقع المفتوحة والمناطق المشجرة لها اعتبارات صيانة وتعرض مختلفة. توثق رولين الظروف الفعلية في الموقع قبل التوصية بالمواد أو حدود الإصلاح.",
    assessmentFocus: [
      "أسطح واسعة ومسار صرف طويل",
      "الوصل بين المنزل والمرآب والفضاء الخارجي المسقوف",
      "الفرق بين المناطق المكشوفة والمحمية بالأشجار",
    ],
    propertyContexts: ["منزل عائلي أكبر", "منزل بهياكل ملحقة", "سكن مفتوح"],
    faqs: [
      {
        question: "هل يمكن تنفيذ المشروع على مراحل حسب مساحة السقف؟",
        answer:
          "أحياناً فقط عندما يسمح نظام السقف والانتقال بنقطة توقف دائمة. يجب أن يشرح النطاق كيف تؤثر المراحل على المناطق المتصلة.",
      },
      {
        question: "هل تحتاج المساحة الكبيرة مادة محددة؟",
        answer:
          "لا. الشكل مدخل واحد. يُنظر معاً في الميل وتصميم نظام السقف والتعرض والمظهر وأولوية الصيانة والميزانية.",
      },
    ],
  },
  "white-rock": {
    intro:
      "منازل وايت روك قد تجمع التعرض للبحر مع أشكال سقف حديثة وشرفات وأجزاء منخفضة الميل. تُراجع الحواف وانتقال الجدار وتفاصيل الصرف في سياق ذلك العمارة.",
    localContext:
      "الظروف الساحلية لا تعني أن منتج سقف واحد صحيح للجميع. تُنظر ملاءمة المنتج وتصميم النظام والتفاصيل المرئية مع الميل وأولوية المالك.",
    assessmentFocus: [
      "تفاصيل محيطية وانتقالات معرضة لمطر مركز",
      "صرف منخفض الميل حول الدرابزين والحافة والجدار المجاور",
      "توافق نظام السقف المختار مع التفاصيل المعدنية المرئية",
    ],
    propertyContexts: ["منزل مطل على البحر", "إقامة حديثة", "تصميم سقف مائل مختلط"],
    faqs: [
      {
        question: "هل التعرض للبحر يستبعد المواد تلقائياً؟",
        answer:
          "ليس تلقائياً. يجب مراجعة النظام كاملاً وقيود المنتج والطلاء أو التشطيب والتثبيت وتفاصيل الانتقال لذلك العقار.",
      },
      {
        question: "لماذا وصلات الشرفة والدرابزين مهمة؟",
        answer:
          "قد تعطل تدفق الماء البسيط وتقدم انتقالات جدار أو حافة أو غشاء. يجب تضمين هذه الواجهات في التقييم الموثق.",
      },
    ],
  },
} as const satisfies Record<ServiceAreaSlug, ServiceAreaContentTranslation>;

export const ui = {
  common: {
    home: "الرئيسية",
    services: "الخدمات",
    serviceAreas: "مناطق الخدمة",
    contact: "تواصل",
    faq: "الأسئلة الشائعة",
    learnMore: "اعرف المزيد",
    requestAssessment: "طلب تقييم السقف",
    exploreServices: "استكشف خدمات السقف",
    viewContactOptions: "عرض كل خيارات التواصل",
  },
  serviceDetail: {
    breadcrumbLabel: "المسار",
    call: "اتصال",
    whoForEyebrow: "لمن هذه الخدمة",
    whoForTitle: "ابدأ بالحالة لا بالمنتج",
    warningSignsTitle: "علامات تحذير شائعة",
    assessmentEyebrow: "انضباط التقييم",
    evaluatesTitle: "ما تقيّمه رولين",
    solutionsEyebrow: "فئات الحلول",
    solutionsTitle: "الخيارات تتبع حالة السقف لا نص المبيعات",
    repairEyebrow: "عندما يكون الإصلاح منطقياً",
    replaceEyebrow: "عندما يعني الاستبدال",
    scopeEyebrow: "النطاق",
    scopeTitle: "ما يؤثر على العمل",
    scopeBody:
      "يُحدد النطاق الفعلي بعد التقييم. لا تنشر رولين روفينغ سعراً أو عمراً أو نظاماً موحداً للسقف لأن ذلك يعتمد على المبنى والنظام المختار.",
    questionsEyebrow: "أسئلة",
    questionsTitle: "قبل أن تقرر",
    nextStepEyebrow: "الخطوة التالية",
    nextStepTitle: "اطلب تقييماً واضحاً لسقفك",
    nextStepBody:
      "أخبرنا بما تراه وأين العقار. نستخدم هذا السياق لتخطيط المحادثة المناسبة التالية.",
  },
  servicesIndex: {
    eyebrow: "خدمات السقف",
    title: "النطاق الصحيح يبدأ بالتشخيص الصحيح",
    body: "استكشف فئات خدمات رولين روفينغ الرئيسية. كل واحدة تبدأ بالمبنى ونظام السقف والظروف التي نراها، لا بتوصية منتج محددة مسبقاً.",
    exploreService: "استكشف الخدمة",
    technicalEyebrow: "مرجع تقني",
    technicalTitle: "مصادر بريتيش كولومبيا الحالية مهمة",
    rcabcTitle: "دليل ممارسات التسقيف RCABC",
    rcabcBody: "مرجع معايير التسقيف الحالية؛ الرابط لا يعني عضوية رولين أو RoofStar.",
    bcCodesTitle: "قوانين بريتيش كولومبيا",
    bcCodesBody: "اللوائح الإقليمية والتعديلات والنشرات للاختصاص ونطاق المشروع.",
    vancouverBylawTitle: "بلدية بناء فانكوفر",
    vancouverBylawBody: "متطلبات فانكوفر الخاصة تُراجع بشكل منفصل عن القانون الإقليمي.",
  },
  areaIndex: {
    breadcrumbLabel: "المسار",
    eyebrow: "مترو فانكوفر واللوير مينلاند",
    title: "مناطق خدمة السقف",
    body: "تقدم رولين روفينغ تقييمات سقف سكنية في المجتمعات أدناه. يُخطط لكل مشروع وفق شكل سقف العقار وتعرضه ووصوله والمتطلبات المحلية.",
    coverageEyebrow: "التغطية لا الفروع",
    coverageTitle: "سياق محلي دون ادعاء مكاتب في كل مدينة",
    coverageBody:
      "توضح هذه الصفحات أين قد تكون تقييمات السقف متاحة. لا تدّعي أن رولين روفينغ تحتفظ بمكتب في كل مدينة مدرجة. تواصل معنا بعنوان العقار لتأكيد تغطية المشروع.",
    nearbyEyebrow: "مجتمع قريب؟",
    nearbyTitle: "اطلب منا تأكيد تغطية المشروع.",
    contactCta: "تواصل مع رولين روفينغ",
  },
  serviceArea: {
    breadcrumbLabel: "المسار",
    metroVancouver: "مترو فانكوفر",
    lowerMainland: "اللوير مينلاند",
    roofingServicesSuffix: "خدمات السقف",
    titlePrefix: "خدمات السقف في {area}",
    confirmedCommunity: "{area} مجتمع خدمة مؤكد. هذه صفحة منطقة خدمة لا ادعاء بفرع محلي.",
    localContextEyebrow: "سياق السقف المحلي",
    localContextTitle: "العقار يحدد التوصية",
    propertyContextsLabel: "سياقات العقار التي نخطط حولها",
    prioritiesEyebrow: "أولويات التقييم",
    prioritiesTitle: "ما الذي يستحق الانتباه في {area}؟",
    planningDisclaimer:
      "هذه معلومات تخطيط عامة لا تشخيصاً عن بُعد أو رأياً هندسياً. تُؤكد النتائج والمتطلبات للعقار المحدد.",
    relatedEyebrow: "خدمات ذات صلة",
    relatedTitle: "قرارات السقف لمنازل {area}",
    relatedBody:
      "ابدأ بالمشكلة الملحوظة وحالة السقف. قد تكون الخطوة التالية إصلاحاً موضعياً أو فحصاً وقائياً أو استبدالاً مخططاً.",
    fallbackServiceName: "خدمة السقف",
    faqSuffix: "الأسئلة الشائعة",
    faqTitle: "أسئلة قبل التقييم",
    requestEyebrow: "طلب تقييم",
    requestTitle: "لنبدأ بالسقف أمامنا.",
    requestBody:
      "أخبرنا بالعقار في {area} وأجزاء السقف وما لاحظته. يساعد هذا في إعداد التقييم؛ لا يحل محل زيارة الموقع.",
  },
  about: {
    eyebrow: "شركة مجموعة رولين",
    title: "خبرة السقف بمنظور شركة بناء",
    body: "رولين روفينغ هو قسم السقف المتخصص في مجموعة رولين، يجلب دقة العمارة والعملية الشفافة والتفكير المنضبط للمشروع من العلامة الأصلية إلى نظام السقف.",
    relationshipEyebrow: "العلاقة",
    relationshipTitle: "متخصصون في التسقيف. مرتبطون بالمبنى كاملاً.",
    relationshipBody1:
      "السقف ليس سطحاً معزولاً. يكمل الجدران والتكسية والنوافذ السقفية والتهوية والصرف والتركيب الداخلي للمبنى. تقترب رولين روفينغ من هذه الواجهات بسياق بناء أوسع.",
    relationshipBody2:
      "يدعم هذا النهج تشخيصاً ونطاقاً واضحين. لا يحل محل الحاجة لمهندس أو مصمم أو متخصص مؤهل عندما تنشأ حالة خارج عمل السقف؛ يساعد في تحديد تلك الحدود مبكراً.",
    principlesEyebrow: "مبادئ العمل",
    principlesTitle: "وضوح عند كل نقطة قرار",
    assessTitle: "قيّم قبل التوصية",
    assessBody:
      "تُؤخذ الأعراض المرئية والمصدر المحتمل وحالة نظام السقف الأوسع في الاعتبار قبل اقتراح نطاق.",
    architectureTitle: "احترم العمارة",
    architectureBody:
      "المواد واللون والملف والحافة والانتقال تساهم في كيف يُقرأ المبنى، لا فقط كيف يتدفق الماء.",
    limitsTitle: "اذكر الحدود",
    limitsBody: "الافتراضات والاستثناءات والشروط التي تحتاج مزيداً من التحقيق ضمن النقاش والنطاق.",
    parentEyebrow: "الشركة الأم",
    parentTitle: "استكشف مجموعة رولين",
    parentBody: "للتجديد والبناء وأعمال المبنى الأوسع، زر موقع الشركة الأم.",
    parentCta: "زيارة RevolinGroup.com",
  },
  beforeAfter: {
    eyebrow: "قبل وبعد",
    title: "معارض الأسقف قبل وبعد في مترو فانكوفر",
    body: "استكشف معارض قبل وبعد من رولين روفينغ لأصحاب المنازل في مترو فانكوفر ولوور مينلاند: من إصلاح التسرب الطارئ إلى استبدال الأسقف الأسفلتية والمعدنية والأرزية والمسطحة، مع سياق واضح للمشكلة والتشخيص والسقف المكتمل.",
    notice:
      "صفِّ حسب الخدمة أو نوع السقف أو المدينة للعثور على التحول الأقرب إلى منزلك، ثم اطلب تقييم سقف لإرشاد يناسب عقارك.",
    projectHeroBody:
      "استكشف تحولات قبل وبعد من رولين روفينغ عبر مترو فانكوفر: من إصلاح التسرب الطارئ إلى استبدال كامل للأسقف الأسفلتية والمعدنية والأرزية والمسطحة. يبرز كل معرض المشكلة والتشخيص والنتيجة النهائية التي يواجهها أصحاب المنازل في لوور مينلاند.",
  },
  faqPage: {
    eyebrow: "إرشاد المالك",
    title: "أسئلة السقف، بإجابات دون نص مبيعات",
    body: "معلومات عملية عن التقييم والإصلاح والاستبدال وأنظمة السقف. المشورة الخاصة بالمشروع تتبع مراجعة في الموقع.",
  },
  contactPage: {
    eyebrow: "تواصل مع رولين روفينغ",
    title: "أخبرنا بما تراه",
    body: "شارك موقع العقار ونوع السقف إن عرفته والمخاوف أو المشروع الذي تفكر فيه. يؤكد النموذج التسليم فقط عندما تقبل نظام استقبال الطلبات المهيأ ذلك.",
    directEyebrow: "تواصل مباشر",
    directTitle: "ابدأ بالأساسيات",
    phone: "هاتف",
    email: "بريد إلكتروني",
    serviceRegion: "منطقة الخدمة",
    regionValue: "مترو فانكوفر واللوير مينلاند",
  },
  legal: {
    legal: "قانوني",
    privacyPolicy: "سياسة الخصوصية",
    websiteTerms: "شروط استخدام الموقع",
    privacyBody: "كيف نتعامل مع استفسارات الموقع وبيانات العقار والتخزين الضروري للمتصفح.",
    termsBody: "القواعد التي تنطبق عند استخدام موقع رولين روفينغ والمواد التعليمية العامة.",
    lastUpdated: "آخر تحديث: ١٠ أغسطس ٢٠٢٦.",
  },
} as const;

export type ContentUi = typeof ui;

// Semantic aliases used by the locale-content registry.
export const serviceContent = services;
export const serviceAreaContent = serviceAreas;

const content = { services, serviceAreas, ui } as const;
export default content;
